import { processSlotImage } from './fotobook_image_processor.js';
import { ColophonService } from '../core/print/cover_core.js';

export default class FotobookEditor {
  constructor() {
    this.currentPage = 1;
    this.selectedPage = 1; // Página seleccionada para aplicar formato
    this.selectedFormats = {};
    this.selectedPhotos = [];
    this.placedPhotos = {}; // { [pageKey]: { [slotIndex]: { url: string, fileIndex: number, photoId?: number } } }
    this.totalRequiredSlots = 0;
    this.totalFilledSlots = 0;
    this.isUploadingSlots = false;
    this.config = window.FOTOBOOK_CONFIG || {};

    // Configuración específica para covers
    this.currentCoverSheet = 1;
    // selectedCoverSheet eliminado - las portadas no son seleccionables, solo editables
    this.selectedCoverFormat = null;
    this.coverConfig = window.COVER_CONFIG || {};
    
    // Servicio de colofón para la última hoja
    this.colophonService = new ColophonService();

    // Ejecutar prueba de cálculo de posiciones
    this.testPositionCalculation();

    // Probar formato de múltiples fotos
    this.testMultiplePhotosFormat();

    // Analizar layouts de múltiples fotos
    this.analyzeMultiplePhotosLayout();

    // Mostrar resumen de asignación cíclica
    this.showCyclicAssignmentSummary();

    // Analizar opciones de configuración
    this.analyzeConfigurationOptions();

    // Mostrar recomendaciones
    this.showConfigurationRecommendations();

    // Inicializar automáticamente si estamos en una página de fotobooks
    if (document.getElementById('fotobook-selection-section')) {
      this.init();
    } else {
    }
  }

  init() {
    this.initializeEventListeners();
    this.renderSheetPreviews();
    this.initializeDefaultFormats();

    // Inicializar covers si hay configuración disponible
    if (
      this.coverConfig &&
      this.coverConfig.covers &&
      this.coverConfig.covers.length > 0
    ) {
      this.initializeDefaultCover();
    }

    // Actualizar dimensiones de página según el paquete seleccionado
    this.updatePageDimensions();

    // Verificar que las dimensiones se aplicaron correctamente
    this.verifyDimensions();

    // Verificar que los templates se están escalando correctamente
    this.verifyTemplateScaling();

    // Verificar que el escalado proporcional funcione correctamente
    this.verifyProportionalScaling();

    // Inicializar indicadores de las páginas del libro
    this.updateBookPageIndicators();

    // Inicializar tooltips de las páginas
    this.updatePageTooltips();

    // Inicializar botón de envío a imprimir (solo para fotobooks)
    this.initializeSendToPrintButton();

    // Diagnóstico de páginas disponibles
    // this.diagnosePageSelection();

    // Si la vista de cuadrícula está visible al inicio, renderizarla
    const gridViewSection = document.getElementById('grid-view-section');
    if (gridViewSection && !gridViewSection.classList.contains('hidden')) {
      this.renderSheetGrid();
    }
  }

  initializeEventListeners() {
    // Navegación de hojas (sheets)
    const prevSheetButton = document.getElementById('prev-sheet');
    const nextSheetButton = document.getElementById('next-sheet');

    if (prevSheetButton) {
      prevSheetButton.addEventListener('click', () => {
        this.navigateSheet(-1);
      });
    } else {
    }

    if (nextSheetButton) {
      nextSheetButton.addEventListener('click', () => {
        this.navigateSheet(1);
      });
    }

    // Toggle Grid View
    const toggleGridBtn = document.getElementById('toggle-grid-view');
    if (toggleGridBtn) {
      toggleGridBtn.addEventListener('click', () => {
        this.renderSheetGrid();
      });
    }

    // Magic Fotobook - Armado Automático
    const magicBtn = document.getElementById('magic-fotobook-btn');
    if (magicBtn) {
      magicBtn.addEventListener('click', () => {
        this.magicFill();
      });
    }

    // Toggle Sheet Formats
    const toggleSheetFormatsBtn = document.getElementById('toggle-sheet-formats');
    if (toggleSheetFormatsBtn) {
      toggleSheetFormatsBtn.addEventListener('click', () => {
        this.toggleSheetFormats();
      });
    }

    // Toggle Cover Formats
    const toggleCoverFormatsBtn = document.getElementById('toggle-cover-formats');
    if (toggleCoverFormatsBtn) {
      toggleCoverFormatsBtn.addEventListener('click', () => {
        this.toggleCoverFormats();
      });
    }

    // Navegación con teclado (flechas izquierda/derecha) solo cuando la sección de interiores está activa
    document.addEventListener('keydown', (e) => {
      // Verificar que la sección de interiores esté activa (no oculta)
      const photosSection = document.getElementById('photos-section');
      if (!photosSection || photosSection.classList.contains('hidden')) {
        return; // No hacer nada si la sección de interiores no está activa
      }

      // No procesar si el usuario está escribiendo en un input, textarea o contenteditable
      const activeElement = document.activeElement;
      const isInputActive =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable ||
          activeElement.closest('[contenteditable="true"]'));

      if (isInputActive) {
        return; // No interferir con la escritura
      }

      // Manejar flechas izquierda (anterior) y derecha (siguiente)
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateSheet(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateSheet(1);
      } else if (e.key === 'Escape') {
        const gridView = document.getElementById('sheets-grid-view');
        if (gridView && !gridView.classList.contains('hidden')) {
          this.toggleGridView();
        }
      }
    });

    // Selector de color de fondo de portada - usar delegación de eventos
    // ya que los botones se crean dinámicamente por CoverSelector
    const paletteContainer = document.getElementById(
      'cover-background-palette'
    );
    if (paletteContainer) {
      paletteContainer.addEventListener('click', (e) => {
        const button = e.target.closest(
          '.palette-color-btn-fotobook, [data-color]'
        );
        if (!button) return;

        e.preventDefault();
        const color = button.getAttribute('data-color');
        if (!color) return;

        // Remover clase active de todos los botones
        paletteContainer
          .querySelectorAll('.palette-color-btn-fotobook, [data-color]')
          .forEach((btn) => {
            btn.classList.remove('active');
          });

        // Agregar clase active al botón clickeado
        button.classList.add('active');

        // Actualizar el input hidden
        const hiddenInput = document.getElementById('cover-background-color');
        if (hiddenInput) {
          hiddenInput.value = color;
        }

        // Actualizar el rect de la portada izquierda
        const coverLeftRect = document.getElementById(
          'cover-left-sheet-background-rect'
        );
        if (coverLeftRect) {
          coverLeftRect.setAttribute('fill', color);
        }

        // Actualizar el rect de la portada derecha también
        const coverRightRect = document.getElementById(
          'cover-right-sheet-background-rect'
        );
        if (coverRightRect) {
          coverRightRect.setAttribute('fill', color);
        }
      });
    }

    // Event listeners para las hojas del libro (sheets) y drop en slots
    const leftSheet = document.getElementById('left-sheet-preview');
    const rightSheet = document.getElementById('right-sheet-preview');

    // Configurar event listener para hoja izquierda
    if (leftSheet) {
      leftSheet.addEventListener('click', () => {
        this.selectBookSheet(1);
      });
    }

    // Configurar event listener para hoja derecha
    if (rightSheet) {
      rightSheet.addEventListener('click', () => {
        this.selectBookSheet(2);
      });
    }

    // Las portadas (covers) no son seleccionables, solo editables
    // No se configuran event listeners para selección de portadas

    // Mantener compatibilidad con elementos .sheet-content y .cover-sheet-container para drop de fotos
    const bookSheets = document.querySelectorAll(
      '.sheet-content, .cover-sheet-container'
    );

    bookSheets.forEach((sheet, index) => {
      // Delegación para manejar dragover/drop sobre el SVG/slots
      sheet.addEventListener('dragover', (ev) => {
        if (
          ev.dataTransfer &&
          ev.dataTransfer.types.includes('text/fotobook-file-index')
        ) {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = 'copy';
        }
      });

      sheet.addEventListener('drop', (ev) => {
        const fileIndex = ev.dataTransfer?.getData('text/fotobook-file-index');
        if (fileIndex === undefined || fileIndex === null || fileIndex === '')
          return;
        ev.preventDefault();

        // Encontrar slot más cercano si se suelta sobre rect/g de slot
        const target = ev.target;
        let slotEl = target.closest
          ? target.closest('.photo-slot, .photo-slot-rect, .cover-photo-slot')
          : null;

        // Fallback específico para portadas: si el drop ocurre dentro del SVG de portada
        // pero no directamente sobre un elemento con clase de slot, buscar el slot
        // más cercano usando la posición del cursor.
        if (!slotEl) {
          const coverSvg =
            sheet.querySelector('#svg-cover-details') ||
            document.getElementById('svg-cover-details');
          if (coverSvg && coverSvg.contains(target)) {
            const slotRects = Array.from(
              coverSvg.querySelectorAll(
                '.photo-slot-rect.cover-photo-slot,[data-slot-index].cover-photo-slot'
              )
            );

            if (slotRects.length > 0) {
              const { clientX, clientY } = ev;
              let best = { el: null, distance: Infinity };

              slotRects.forEach((el) => {
                const rect = el.getBoundingClientRect();

                // Si el punto del drop está dentro del rect, lo usamos directamente
                if (
                  clientX >= rect.left &&
                  clientX <= rect.right &&
                  clientY >= rect.top &&
                  clientY <= rect.bottom
                ) {
                  best = { el, distance: 0 };
                  return;
                }

                // Si no está dentro, calcular distancia mínima al borde del rect
                const dx =
                  clientX < rect.left
                    ? rect.left - clientX
                    : clientX > rect.right
                    ? clientX - rect.right
                    : 0;
                const dy =
                  clientY < rect.top
                    ? rect.top - clientY
                    : clientY > rect.bottom
                    ? clientY - rect.bottom
                    : 0;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < best.distance) {
                  best = { el, distance };
                }
              });

              if (best.el) {
                slotEl = best.el;
              }
            }
          }
        }

        if (!slotEl) return;

        const slotIndex = slotEl.getAttribute('data-slot-index');
        if (slotIndex === null) return;

        this.handlePhotoDropOnSlot(
          sheet,
          parseInt(slotIndex, 10),
          parseInt(fileIndex, 10)
        );
      });
    });

    // Selección de formatos de sheets - ahora aplica directamente a la hoja activa
    const sheetFormatOptions = document.querySelectorAll(
      '.sheet-format-option'
    );

    sheetFormatOptions.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation(); // Evitar que el evento se propague y deseleccione la hoja
        e.preventDefault(); // Evitar comportamiento por defecto
        this.selectSheetFormat(option);
      });
    });

    // Selección de formatos de covers - aplica a la portada activa
    const coverFormatOptions = document.querySelectorAll(
      '.cover-format-option'
    );

    coverFormatOptions.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation(); // Evitar que el evento se propague
        e.preventDefault(); // Evitar comportamiento por defecto
        this.selectCoverFormat(option);
      });
    });
  }

  initializeSendToPrintButton() {
    const fotobookSection = document.getElementById('fotobook-selection-section');
    const printButton = document.getElementById('fotobook-send-to-print-btn');
    const orderForm = document.getElementById('fotobook-order-form');
    const photosField = document.getElementById('fotobook-photos-json');
    const layoutField = document.getElementById('fotobook-layout-json');
    const paymentMessage = document.getElementById('fotobook-payment-message');
    const paymentProcessing = document.getElementById('fotobook-payment-processing');

    if (!printButton) {
      console.warn(
        'FotobookEditor: Send to print button not found for fotobook flow'
      );
      return;
    }

    console.log('FotobookEditor: initializeSendToPrintButton', {
      buttonFound: !!printButton,
      orderFormFound: !!orderForm,
      photosFieldFound: !!photosField,
      layoutFieldFound: !!layoutField,
      paymentMessageFound: !!paymentMessage,
      paymentProcessingFound: !!paymentProcessing,
      sectionFound: !!fotobookSection
    });

    // Deshabilitar por defecto
    printButton.classList.add('disabled');
    printButton.disabled = true;

    orderForm?.addEventListener('submit', () => {
      console.log('FotobookEditor: fotobook-order-form submit triggered', {
        packageId: orderForm.querySelector('[name="order[package_id]"]')?.value,
        photosLength: photosField?.value?.length || 0,
        layoutLength: layoutField?.value?.length || 0
      });
    });

    printButton.addEventListener('click', async (e) => {
      e.preventDefault();

      console.log('FotobookEditor: Send to print button clicked', {
        buttonId: printButton.id,
        canSendToPrint: this.canSendToPrint(),
        totalRequiredSlots: this.totalRequiredSlots,
        totalFilledSlots: this.totalFilledSlots,
        isUploadingSlots: this.isUploadingSlots
      });

      if (!this.canSendToPrint()) {
        const validation = this.getSendToPrintValidation();
        console.warn(
          'FotobookEditor: Cannot send to print, layout not complete or uploads pending',
          validation
        );
        this.showSendToPrintValidationAlert(validation);
        return;
      }

      // Visual feedback de procesamiento
      printButton.disabled = true;
      printButton.classList.add('disabled');
      if (paymentMessage) paymentMessage.style.display = 'none';
      if (paymentProcessing) paymentProcessing.style.display = 'flex';

      try {
        console.log(
          'FotobookEditor: Starting slot processing before submit'
        );
        await this.processAndUploadAllSlots();
        const layoutJson = this.buildFotobookLayoutJson();

        // Guardar layout para debug / herramientas
        window.FOTOBOOK_LAYOUT = layoutJson;
        console.log('FotobookEditor: Layout built successfully', layoutJson);

        // Construir lista plana de IDs de fotos para compatibilidad con flujo existente
        const photoIds = [];
        layoutJson.pages.forEach((pageEntry) => {
          pageEntry.slots.forEach((slot) => {
            const slotPhotoId = slot.photoId || slot.sourcePhotoId;
            if (slotPhotoId && !photoIds.includes(String(slotPhotoId))) {
              photoIds.push(String(slotPhotoId));
            }
          });
        });

        (layoutJson.covers?.slots || []).forEach((slot) => {
          const slotPhotoId = slot.photoId || slot.sourcePhotoId;
          if (slotPhotoId && !photoIds.includes(String(slotPhotoId))) {
            photoIds.push(String(slotPhotoId));
          }
        });

        const photosValue = photoIds.join(',');
        console.log('FotobookEditor: Prepared form payload', {
          photoIds,
          photosValue,
          pageCount: layoutJson.pages.length,
          coverKeys: Object.keys(layoutJson.covers || {})
        });

        if (!orderForm || !photosField || !layoutField) {
          console.error(
            'FotobookEditor: Order form or fields not found, cannot submit'
          );
          console.error('FotobookEditor: orderForm:', orderForm);
          console.error('FotobookEditor: photosField:', photosField);
          console.error('FotobookEditor: layoutField:', layoutField);
          // Restaurar estado visual
          if (paymentMessage) paymentMessage.style.display = 'flex';
          if (paymentProcessing) paymentProcessing.style.display = 'none';
          printButton.disabled = false;
          printButton.classList.remove('disabled');
          return;
        }

        // Rellenar campos ocultos y enviar al controlador (CartsController#create_order)
        photosField.value = photosValue;
        layoutField.value = JSON.stringify(layoutJson);

        console.log('FotobookEditor: Submitting fotobook order form', {
          action: orderForm.action,
          method: orderForm.method,
          photosValue,
          layoutPreview: layoutField.value
        });

        // Asegurar que el formulario se envíe correctamente
        orderForm.submit();
      } catch (error) {
        console.error(
          'FotobookEditor: Error processing / uploading slots before print:',
          error
        );
        console.error('FotobookEditor: Error stack:', error.stack);
        // Restaurar estado visual en caso de error
        if (paymentMessage) paymentMessage.style.display = 'flex';
        if (paymentProcessing) paymentProcessing.style.display = 'none';
        printButton.disabled = false;
        printButton.classList.remove('disabled');
      }
    });
  }

  canSendToPrint() {
    const validation = this.getSendToPrintValidation();

    if (validation.interiorFilledSlots < validation.totalRequiredSlots) {
      console.log(
        'FotobookEditor: canSendToPrint blocked by incomplete interior slots',
        validation
      );
      return false;
    }

    if (validation.coverFilledSlots < validation.requiredCoverSlots) {
      console.log(
        'FotobookEditor: canSendToPrint blocked by incomplete cover slots',
        validation
      );
      return false;
    }

    if (validation.isUploadingSlots) {
      console.log('FotobookEditor: canSendToPrint blocked by active upload');
      return false;
    }

    return true;
  }

  getRequiredCoverSlots() {
    const configured = parseInt(this.selectedCoverFormat?.noPhotos, 10);
    if (Number.isNaN(configured) || configured < 0) return 0;
    return configured;
  }

  countCoverFilledSlots() {
    const coverSlots = this.placedPhotos['cover-front'] || {};
    let filled = 0;

    Object.values(coverSlots).forEach((slotData) => {
      if (slotData && typeof slotData.fileIndex === 'number') {
        filled += 1;
      }
    });

    return filled;
  }

  getSendToPrintValidation() {
    if (!this.totalRequiredSlots) {
      this.totalRequiredSlots = this.calculateTotalRequiredSlots();
    }

    const slotSummary = this.countFilledSlots();
    const requiredCoverSlots = this.getRequiredCoverSlots();
    const coverFilledSlots = this.countCoverFilledSlots();
    const interiorFilledSlots = Math.max(
      0,
      slotSummary.filledSlots - coverFilledSlots
    );

    return {
      totalRequiredSlots: this.totalRequiredSlots,
      interiorFilledSlots,
      requiredCoverSlots,
      coverFilledSlots,
      isUploadingSlots: this.isUploadingSlots,
      filledWithIds: slotSummary.filledWithIds
    };
  }

  showSendToPrintValidationAlert(validation) {
    const missingInterior = Math.max(
      0,
      validation.totalRequiredSlots - validation.interiorFilledSlots
    );
    const missingCover = Math.max(
      0,
      validation.requiredCoverSlots - validation.coverFilledSlots
    );

    const reasons = [];
    if (missingInterior > 0) {
      reasons.push(`faltan ${missingInterior} foto(s) en interiores`);
    }
    if (missingCover > 0) {
      reasons.push(`falta(n) ${missingCover} foto(s) en portada`);
    }
    if (validation.isUploadingSlots) {
      reasons.push('hay fotos procesándose todavía');
    }

    if (reasons.length === 0) return;

    const message = `No se puede enviar a imprimir: ${reasons.join(', ')}.`;
    if (window.appAlerts?.warning) {
      window.appAlerts.warning(message);
    } else {
      alert(message);
    }
  }

  calculateTotalRequiredSlots() {
    let total = 0;
    const processedPages = new Set();

    // Verificar que tenemos formatos inicializados
    if (Object.keys(this.selectedFormats).length === 0) {
      console.warn('FotobookEditor: No formats found when calculating total required slots');
      return 0;
    }

    for (let page = 1; page <= this.config.totalSheets; page++) {
      // La última hoja está reservada para el colofón: no requiere fotos del usuario
      if (page === this.config.totalSheets) continue;

      // Si esta página ya fue procesada como parte de un formato de doble página, saltarla
      if (processedPages.has(page)) continue;

      const formatInfo = this.selectedFormats[page];
      if (!formatInfo) {
        console.warn(`FotobookEditor: No format found for page ${page}`);
        continue;
      }

      // Usar el número de slots real del layout config como fuente de verdad
      // para que coincida con lo que addPhotoToFirstEmptySlot puede rellenar
      const layoutData = formatInfo.config ? this.parseLayoutData(formatInfo.config) : null;
      const slots = layoutData ? layoutData.length : (parseInt(formatInfo.noPhotos, 10) || 0);

      // Si es formato de doble página, verificar que la siguiente página también tenga el mismo formato
      if (formatInfo.double === true && page < this.config.totalSheets) {
        const nextPageFormat = this.selectedFormats[page + 1];
        // Solo contar si la siguiente página tiene el mismo formato (confirmando que es realmente doble página)
        if (
          nextPageFormat &&
          nextPageFormat.id === formatInfo.id &&
          nextPageFormat.double === true
        ) {
          // Contar los slots solo una vez (no duplicar)
          total += Math.max(0, slots);
          // Marcar ambas páginas como procesadas
          processedPages.add(page);
          processedPages.add(page + 1);
        } else {
          // Si no hay formato de doble página completo, contar como página individual
          total += Math.max(0, slots);
          processedPages.add(page);
        }
      } else {
        // Formato de página individual
        total += Math.max(0, slots);
        processedPages.add(page);
      }
    }

    console.log('FotobookEditor: calculateTotalRequiredSlots result', {
      total,
      pages: Array.from(processedPages).sort((a, b) => a - b)
    });

    return total;
  }

  getTotalSlotsBeforePage(pageNumber) {
    let total = 0;
    for (let p = 1; p < pageNumber; p++) {
      const format = this.selectedFormats[p];
      if (format) {
        const layoutData = format.config ? this.parseLayoutData(format.config) : null;
        total += layoutData ? layoutData.length : (parseInt(format.noPhotos, 10) || 0);
      }
    }
    return total;
  }

  countFilledSlots() {
    // Iteramos páginas de la misma manera que calculateTotalRequiredSlots para
    // garantizar consistencia: mismo conteo base y sin doble-conteo en
    // formatos de doble página (donde la foto queda guardada en ambas páginas).
    const processedPages = new Set();
    let interiorFilled = 0;
    let filledWithIds = true;

    if (Object.keys(this.selectedFormats).length > 0) {
      for (let page = 1; page <= this.config.totalSheets; page++) {
        if (page === this.config.totalSheets) continue; // colofón
        if (processedPages.has(page)) continue;

        const formatInfo = this.selectedFormats[page];
        if (!formatInfo) continue;

        const layoutData = formatInfo.config ? this.parseLayoutData(formatInfo.config) : null;
        const numSlots = layoutData ? layoutData.length : (parseInt(formatInfo.noPhotos, 10) || 0);

        // Marcar páginas procesadas (igual que calculateTotalRequiredSlots)
        if (formatInfo.double === true && page < this.config.totalSheets) {
          const nextPageFormat = this.selectedFormats[page + 1];
          if (nextPageFormat && nextPageFormat.id === formatInfo.id && nextPageFormat.double === true) {
            processedPages.add(page);
            processedPages.add(page + 1);
          } else {
            processedPages.add(page);
          }
        } else {
          processedPages.add(page);
        }

        // Contar slots rellenados en la página canónica (izquierda del spread)
        const pagePlaced = this.placedPhotos[page] || {};
        for (let slotIndex = 0; slotIndex < numSlots; slotIndex++) {
          const slotData = pagePlaced[slotIndex];
          if (slotData && typeof slotData.fileIndex === 'number') {
            interiorFilled += 1;
            if (!slotData.photoId && !slotData.sourcePhotoId) {
              filledWithIds = false;
            }
          }
        }
      }
    }

    // Contar slots de portada por separado (se restan en getSendToPrintValidation)
    let coverFilled = 0;
    const coverSlots = this.placedPhotos['cover-front'] || {};
    Object.values(coverSlots).forEach((slotData) => {
      if (slotData && typeof slotData.fileIndex === 'number') {
        coverFilled += 1;
      }
    });

    this.totalFilledSlots = interiorFilled + coverFilled;

    return { filledSlots: interiorFilled + coverFilled, filledWithIds };
  }

  updateSendToPrintButtonState() {
    const printButton = document.getElementById('fotobook-send-to-print-btn');
    if (!printButton) return;

    // Recalcular slots requeridos si no están inicializados
    if (!this.totalRequiredSlots || this.totalRequiredSlots <= 0) {
      this.totalRequiredSlots = this.calculateTotalRequiredSlots();

      // Si aún no hay formatos inicializados, intentar inicializarlos ahora
      if (!this.totalRequiredSlots || this.totalRequiredSlots <= 0) {
        const formatOptions = document.querySelectorAll('.sheet-format-option');
        if (formatOptions.length > 0 && Object.keys(this.selectedFormats).length === 0) {
          console.warn('FotobookEditor: Detected uninitialized formats, attempting initialization...');
          // Reintentar la inicialización si aún no hay formatos
          this.retryInitializeFormats();
        }
      }
    }

    const canSend = this.canSendToPrint();
    const validation = this.getSendToPrintValidation();

    console.log('FotobookEditor: updateSendToPrintButtonState', {
      canSend,
      totalRequiredSlots: validation.totalRequiredSlots,
      totalFilledSlots: this.totalFilledSlots,
      interiorFilledSlots: validation.interiorFilledSlots,
      requiredCoverSlots: validation.requiredCoverSlots,
      coverFilledSlots: validation.coverFilledSlots,
      isUploadingSlots: validation.isUploadingSlots,
      selectedFormatsCount: Object.keys(this.selectedFormats).length,
      placedPhotosPages: Object.keys(this.placedPhotos).length,
      placedPhotosDetail: Object.entries(this.placedPhotos).map(([page, slots]) => ({
        page,
        count: Object.keys(slots).length
      }))
    });

    printButton.disabled = !canSend;
    if (canSend) {
      printButton.classList.remove('disabled');
      console.log('FotobookEditor: Print button ENABLED - all slots filled');
    } else {
      printButton.classList.add('disabled');
      console.log('FotobookEditor: Print button DISABLED - reason:', {
        interior_slots_missing:
          validation.interiorFilledSlots < validation.totalRequiredSlots
            ? `${validation.totalRequiredSlots - validation.interiorFilledSlots} interior slots still empty`
            : null,
        cover_slots_missing:
          validation.coverFilledSlots < validation.requiredCoverSlots
            ? `${validation.requiredCoverSlots - validation.coverFilledSlots} cover slots still empty`
            : null,
        uploading: validation.isUploadingSlots ? 'uploading in progress' : null,
        no_formats:
          validation.totalRequiredSlots === 0
            ? 'no formats initialized'
            : null
      });
    }
  }

  retryInitializeFormats() {
    const formatOptions = document.querySelectorAll('.sheet-format-option');

    if (formatOptions.length === 0) {
      console.warn('FotobookEditor: Still no format options found, will retry later');
      return;
    }

    console.log('FotobookEditor: Retrying format initialization with available options');
    const formatsArray = Array.from(formatOptions);

    // Asignar formatos cíclicamente a todas las páginas
    for (let page = 1; page <= this.config.totalSheets; page++) {
      if (!this.selectedFormats[page]) {
        const formatIndex = (page - 1) % formatsArray.length;
        const selectedFormat = formatsArray[formatIndex];

        const formatInfo = {
          id: selectedFormat.dataset.sheetId,
          noPhotos: parseInt(selectedFormat.dataset.noPhotos, 10) || 0,
          fullPage: selectedFormat.dataset.fullPage === 'true',
          double: selectedFormat.dataset.double === 'true',
          config: selectedFormat.dataset.config,
          isCover: selectedFormat.dataset.isCover === 'true',
          textConfig: this.buildCoverTextConfigFromDataset(selectedFormat.dataset)
        };

        this.selectedFormats[page] = formatInfo;
      }
    }

    // Recalcular slots después de la reinicialización
    this.totalRequiredSlots = this.calculateTotalRequiredSlots();
    console.log('FotobookEditor: Format reinitialization completed', {
      totalRequiredSlots: this.totalRequiredSlots,
      selectedFormatsCount: Object.keys(this.selectedFormats).length
    });
  }

  async processAndUploadAllSlots() {
    if (this.isUploadingSlots) {
      console.warn(
        'FotobookEditor: processAndUploadAllSlots skipped because upload is already in progress'
      );
      return;
    }
    this.isUploadingSlots = true;
    this.updateSendToPrintButtonState();

    try {
      const uploadPromises = [];

      Object.entries(this.placedPhotos).forEach(([pageKey, slotsByIndex]) => {
        Object.entries(slotsByIndex).forEach(([slotIndex, slotData]) => {
          const { fileIndex, photoId } = slotData;
          if (photoId) return;

          const photo =
            window.fotobookPhotoSelector?.getSelectedFiles?.()[fileIndex];
          if (!photo || photo.status !== 'ready') return;

          const file = photo.file;

          const pageEl = this.findPageElementByKey(pageKey);
          if (!pageEl) return;
          const svg = pageEl.querySelector('svg') || pageEl.closest('svg');
          if (!svg) return;

          const slotRect =
            svg.querySelector(
              `.photo-slot-rect[data-slot-index="${slotIndex}"]`
            ) ||
            svg.querySelector(
              `.cover-photo-slot[data-slot-index="${slotIndex}"]`
            );
          if (!slotRect) return;

          const slotBox = this.getSlotBox(slotRect);
          console.log('FotobookEditor: Queueing slot upload', {
            pageKey,
            slotIndex,
            fileName: file.name,
            hasExistingPhotoId: !!photoId,
            slotBox
          });
          uploadPromises.push(
            this.processAndUploadSingleSlot(
              pageKey,
              parseInt(slotIndex, 10),
              file,
              slotBox
            )
          );
        });
      });

      console.log('FotobookEditor: Waiting for slot uploads', {
        uploadsQueued: uploadPromises.length
      });
      await Promise.all(uploadPromises);
      console.log('FotobookEditor: Slot uploads completed');
    } finally {
      this.isUploadingSlots = false;
      this.updateSendToPrintButtonState();
    }
  }

  findPageElementByKey(pageKey) {
    const candidates = document.querySelectorAll(
      '.sheet-content, .cover-sheet-container'
    );
    for (const el of candidates) {
      if (el.dataset && el.dataset.pageKey === String(pageKey)) {
        return el;
      }
    }
    const numericKey = parseInt(pageKey, 10);
    if (!Number.isNaN(numericKey)) {
      if (numericKey === this.currentPage) {
        return document.getElementById('left-sheet-preview');
      }
      if (numericKey === this.currentPage + 1) {
        return document.getElementById('right-sheet-preview');
      }
    }
    return null;
  }

  async processAndUploadSingleSlot(pageKey, slotIndex, file, slotBox) {
    try {
      console.log('FotobookEditor: processAndUploadSingleSlot start', {
        pageKey,
        slotIndex,
        fileName: file?.name,
        slotBox
      });
      const { blob, width, height } = await processSlotImage(file, slotBox, {
        quality: 0.9,
        dpi: 300
      });

      console.log('FotobookEditor: Slot image processed', {
        pageKey,
        slotIndex,
        width,
        height,
        blobSize: blob?.size
      });
      const photoId = await this.uploadSlotBlob(blob, file.name);

      if (!this.placedPhotos[pageKey]) this.placedPhotos[pageKey] = {};
      const existing = this.placedPhotos[pageKey][slotIndex] || {};
      this.placedPhotos[pageKey][slotIndex] = {
        ...existing,
        photoId
      };

      console.log('FotobookEditor: Slot upload completed', {
        pageKey,
        slotIndex,
        photoId
      });
    } catch (error) {
      console.error('FotobookEditor: Error processing/uploading slot', {
        pageKey,
        slotIndex,
        error
      });
      throw error;
    }
  }

  async uploadSlotBlob(blob, originalName) {
    const formData = new FormData();
    formData.append('filepond', blob, originalName || 'fotobook-slot.jpg');

    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfMeta
      ? csrfMeta.getAttribute('content')
      : window.CSRF_TOKEN || '';

    const response = await fetch('/photos/upload', {
      method: 'POST',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      body: formData
    });

    console.log('FotobookEditor: uploadSlotBlob response received', {
      originalName,
      status: response.status,
      ok: response.ok
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(
        `Error al subir la foto de fotobook: ${response.status} ${text}`
      );
    }

    const payload = await response.clone().json().catch(() => null);

    if (payload && payload.id) {
      const photoIdFromJson = parseInt(payload.id, 10);
      if (!Number.isNaN(photoIdFromJson)) {
        return photoIdFromJson;
      }
    }

    const text = await response.text().catch(() => '');
    const photoId = parseInt(text, 10);
    if (Number.isNaN(photoId)) {
      throw new Error(
        `Respuesta inválida al subir la foto de fotobook: "${text}"`
      );
    }
    return photoId;
  }

  buildFotobookLayoutJson() {
    const normalizePhotoId = (rawId) => {
      if (rawId === null || rawId === undefined) return null;

      const value = String(rawId).trim();
      if (!value) return null;
      if (['null', 'nil', 'undefined'].includes(value.toLowerCase())) return null;

      return value;
    };

    const layout = {
      productId: this.config.productId,
      packageId: this.config.packageId,
      totalSheets: this.config.totalSheets,
      totalPhysicalSheets: this.config.totalPhysicalSheets,
      // Dimensiones de la hoja en píxeles (a 300 dpi) para recreación en el backend
      pageWidth: this.config.pageWidth || null,
      pageHeight: this.config.pageHeight || null,
      paperName: this.config.paperName || null,
      pages: [],
      covers: {}
    };

    // Construir páginas con formatos, proporciones y fotos asignadas
    for (let page = 1; page <= this.config.totalSheets; page++) {
      const formatInfo = this.selectedFormats[page];
      const slotsByIndex = this.placedPhotos[page] || {};

      // Obtener layout real del config para incluir posiciones y tamaños por slot
      const layoutData = formatInfo?.config ? this.parseLayoutData(formatInfo.config) : null;

      const pageEntry = {
        page,
        formatId: formatInfo ? parseInt(formatInfo.id, 10) : null,
        noPhotos: layoutData ? layoutData.length : (formatInfo ? parseInt(formatInfo.noPhotos, 10) : 0),
        fullPage: !!(formatInfo && formatInfo.fullPage),
        double: !!(formatInfo && formatInfo.double),
        // Config raw para que el backend pueda reconstruir el layout
        config: formatInfo?.config || null,
        // Array de proporciones por slot (posición y tamaño en % sobre la hoja, origen centro)
        slotLayout: layoutData
          ? layoutData.map((slot) => ({
              type: slot.type || 'r',
              x: slot.x,
              y: slot.y,
              width: slot.width,
              height: slot.height
            }))
          : [],
        slots: []
      };

      Object.entries(slotsByIndex).forEach(([slotIndex, slotData]) => {
        if (!slotData) return;

        const photoId = normalizePhotoId(slotData.photoId);
        const sourcePhotoId = normalizePhotoId(
          slotData.sourcePhotoId || slotData.photoId
        );

        if (!photoId && !sourcePhotoId) return;

        const idx = parseInt(slotIndex, 10);
        // Incluir proporciones de posición específica de este slot
        const slotPosition = layoutData && layoutData[idx]
          ? {
              type: layoutData[idx].type || 'r',
              x: layoutData[idx].x,
              y: layoutData[idx].y,
              width: layoutData[idx].width,
              height: layoutData[idx].height
            }
          : null;

        pageEntry.slots.push({
          slotIndex: idx,
          photoId,
          sourcePhotoId,
          fileIndex: parseInt(slotData.fileIndex, 10),
          url: slotData.url || null,
          largeUrl: slotData.url ? slotData.url.replace('/thumb_', '/large_') : null,
          // Posición y tamaño del slot (% sobre la hoja, x/y = centro del slot)
          position: slotPosition
        });
      });

      layout.pages.push(pageEntry);
    }

    // Incluir datos de las covers si están disponibles
    if (this.selectedCoverFormat && window.coverSelector) {
      const coverSelector = window.coverSelector;
      const coverSlotsByIndex = this.placedPhotos['cover-front'] || {};
      const coverSlots = [];

      Object.entries(coverSlotsByIndex).forEach(([slotIndex, slotData]) => {
        if (!slotData) return;

        const photoId = normalizePhotoId(slotData.photoId);
        const sourcePhotoId = normalizePhotoId(
          slotData.sourcePhotoId || slotData.photoId
        );

        if (!photoId && !sourcePhotoId) return;

        coverSlots.push({
          slotIndex: parseInt(slotIndex, 10),
          photoId,
          sourcePhotoId,
          fileIndex: parseInt(slotData.fileIndex, 10),
          largeUrl: slotData.url ? slotData.url.replace('/thumb_', '/large_') : null
        });
      });

      const coverData = {
        coverId: this.selectedCoverFormat.id
          ? parseInt(this.selectedCoverFormat.id, 10)
          : null,
        title: coverSelector.titleInput
          ? coverSelector.titleInput.value.trim()
          : '',
        subtitle: coverSelector.subtitleInput
          ? coverSelector.subtitleInput.value.trim()
          : '',
        spine: coverSelector.spineInput
          ? coverSelector.spineInput.value.trim()
          : '',
        backgroundColor: coverSelector.bgInput
          ? coverSelector.bgInput.value
          : '',
        textColor: coverSelector.currentTextColor || '',
        fontFamily: coverSelector.fontSelect
          ? coverSelector.fontSelect.value
          : '',
        // Configuración del formato de cover
        coverTitle: this.selectedCoverFormat.coverTitle || '',
        coverSubtitle: this.selectedCoverFormat.coverSubtitle || '',
        coverTitleX: this.selectedCoverFormat.coverTitleX || 0.5,
        coverTitleY: this.selectedCoverFormat.coverTitleY || 0.3,
        coverTitleSize: this.selectedCoverFormat.coverTitleSize || 0.1,
        coverSubtitleX: this.selectedCoverFormat.coverSubtitleX || 0.5,
        coverSubtitleY: this.selectedCoverFormat.coverSubtitleY || 0.4,
        coverSubtitleSize: this.selectedCoverFormat.coverSubtitleSize || 0.05,
        fontFamilies: this.selectedCoverFormat.fontFamilies || '',
        palette: this.selectedCoverFormat.palette || '',
        slots: coverSlots
      };
      layout.covers = coverData;
    }

    console.log('FotobookEditor: buildFotobookLayoutJson summary', {
      pages: layout.pages.length,
      coversPresent: Object.keys(layout.covers || {}).length > 0
    });
    return layout;
  }

  handlePhotoDropOnSlot(pageEl, slotIndex, fileIndex) {
    // Verificar si es el contenedor de portada — las portadas siempre pueden recibir fotos
    const isCoverElement = pageEl && pageEl.id && pageEl.id.includes('cover');

    if (!isCoverElement) {
      // Solo bloquear la última hoja (colofón) para páginas interiores
      const isLeftSheet = pageEl.id === 'left-sheet-preview';
      const currentPageNumber = isLeftSheet
        ? this.currentPage
        : this.currentPage + 1;

      if (currentPageNumber === this.config.totalSheets) {
        // La última hoja está reservada para el colofón, no se pueden colocar fotos
        return;
      }
    }

    // Obtener el archivo desde el selector global si existe
    if (!window.fotobookPhotoSelector) {
      console.warn('FotobookEditor: fotobookPhotoSelector no encontrado');
      return;
    }

    const photo = window.fotobookPhotoSelector.getSelectedFiles()[fileIndex];
    if (!photo || photo.status !== 'ready') return;

    const file = photo.file || null;

    const svg = pageEl.querySelector('svg') || pageEl.closest('svg');
    if (!svg) return;

    // Buscar el rect/círculo del slot por data-slot-index
    const slotRect =
      svg.querySelector(`.photo-slot-rect[data-slot-index="${slotIndex}"]`) ||
      svg.querySelector(`.cover-photo-slot[data-slot-index="${slotIndex}"]`);
    if (!slotRect) return;

    const { x, y, w, h } = this.getSlotBox(slotRect);
    if ([x, y, w, h].some((v) => Number.isNaN(v))) {
      console.warn('FotobookEditor: slot box invalid on drop', {
        slotIndex,
        slotRect
      });
      return;
    }

    // Crear/actualizar grupo de slot para contener la imagen
    const slotGroup =
      svg.querySelector(`g.photo-slot[data-slot-index="${slotIndex}"]`) ||
      svg.querySelector(`.cover-photo-slot[data-slot-index="${slotIndex}"]`);
    if (!slotGroup) return;

    // Remover imagen previa dentro del slot si existe
    const prevImg = slotGroup.querySelector('image');
    if (prevImg) prevImg.remove();

    // Usar URL local del archivo para render estable (incluido export a PDF), con fallback.
    const objectUrl =
      photo.localUrl ||
      photo.url ||
      photo.thumbUrl ||
      (file ? URL.createObjectURL(file) : null);
    if (!objectUrl) {
      console.warn('FotobookEditor: no preview URL available for selected photo', {
        fileIndex,
        photoId: photo.id
      });
      return;
    }
    if (!photo.localUrl && file) {
      photo.localUrl = objectUrl;
    }

    const pageKey = this.resolvePageKey(pageEl);
    const clipId = this.createClipPathForSlot(
      svg,
      slotRect,
      pageKey,
      slotIndex
    );

    // Crear elemento <image> SVG y hacerlo cubrir el slot (object-fit: cover)
    const imgEl = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'image'
    );
    imgEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', objectUrl);
    imgEl.setAttribute('x', x.toFixed(2));
    imgEl.setAttribute('y', y.toFixed(2));
    imgEl.setAttribute('width', w.toFixed(2));
    imgEl.setAttribute('height', h.toFixed(2));
    imgEl.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    imgEl.setAttribute('data-file-index', String(fileIndex));
    if (clipId) imgEl.setAttribute('clip-path', `url(#${clipId})`);

    // Insertar la imagen después del rect para que cubra el área
    slotGroup.appendChild(imgEl);

    // En portadas, garantizar que el texto quede siempre por encima de las fotos
    if (svg.id === 'svg-cover-details') {
      this.bringCoverTextToFront(svg);
    }

    // Determinar número de hoja real desde el elemento
    if (!this.placedPhotos[pageKey]) this.placedPhotos[pageKey] = {};
    this.placedPhotos[pageKey][slotIndex] = {
      url: objectUrl,
      fileIndex,
      sourcePhotoId: photo.id || null,
      photoId: !file && photo.id ? photo.id : null
    };

    // Verificar si la página está en formato de doble página y colocar la foto también en la página adyacente
    const leftPageFormat = this.selectedFormats[this.currentPage];
    const rightPageFormat = this.selectedFormats[this.currentPage + 1];
    const isDoublePageFormat =
      leftPageFormat &&
      rightPageFormat &&
      leftPageFormat.id === rightPageFormat.id &&
      leftPageFormat.double === true;

    if (isDoublePageFormat && currentPageNumber <= this.config.totalSheets) {
      // Determinar la página adyacente
      const adjacentPageNumber = isLeftSheet
        ? this.currentPage + 1
        : this.currentPage;

      // Solo proceder si la página adyacente existe
      if (adjacentPageNumber <= this.config.totalSheets) {
        // Encontrar el elemento SVG de la página adyacente
        const adjacentSheetId = isLeftSheet
          ? 'right-sheet-preview'
          : 'left-sheet-preview';
        const adjacentSheetEl = document.getElementById(adjacentSheetId);

        if (adjacentSheetEl) {
          const adjacentSvg = adjacentSheetEl.querySelector('svg');
          if (adjacentSvg) {
            // Buscar el slot correspondiente en la página adyacente
            const adjacentSlotRect = adjacentSvg.querySelector(
              `.photo-slot-rect[data-slot-index="${slotIndex}"]`
            );
            if (adjacentSlotRect) {
              const adjacentSlotGroup = adjacentSvg.querySelector(
                `g.photo-slot[data-slot-index="${slotIndex}"]`
              );
              if (adjacentSlotGroup) {
                // Remover imagen previa si existe
                const adjacentPrevImg =
                  adjacentSlotGroup.querySelector('image');
                if (adjacentPrevImg) adjacentPrevImg.remove();

                // Obtener dimensiones del slot adyacente
                const adjacentSlotBox = this.getSlotBox(adjacentSlotRect);
                if (
                  ![
                    adjacentSlotBox.x,
                    adjacentSlotBox.y,
                    adjacentSlotBox.w,
                    adjacentSlotBox.h
                  ].some((v) => Number.isNaN(v))
                ) {
                  // Crear elemento de imagen para la página adyacente
                  const adjacentImgEl = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'image'
                  );
                  adjacentImgEl.setAttributeNS(
                    'http://www.w3.org/1999/xlink',
                    'href',
                    objectUrl
                  );
                  adjacentImgEl.setAttribute('x', adjacentSlotBox.x.toFixed(2));
                  adjacentImgEl.setAttribute('y', adjacentSlotBox.y.toFixed(2));
                  adjacentImgEl.setAttribute(
                    'width',
                    adjacentSlotBox.w.toFixed(2)
                  );
                  adjacentImgEl.setAttribute(
                    'height',
                    adjacentSlotBox.h.toFixed(2)
                  );
                  adjacentImgEl.setAttribute(
                    'preserveAspectRatio',
                    'xMidYMid slice'
                  );
                  adjacentImgEl.setAttribute(
                    'data-file-index',
                    String(fileIndex)
                  );

                  // Crear clip path para el slot adyacente
                  const adjacentPageKey = isLeftSheet
                    ? this.currentPage + 1
                    : this.currentPage;
                  const adjacentClipId = this.createClipPathForSlot(
                    adjacentSvg,
                    adjacentSlotRect,
                    adjacentPageKey,
                    slotIndex
                  );
                  if (adjacentClipId)
                    adjacentImgEl.setAttribute(
                      'clip-path',
                      `url(#${adjacentClipId})`
                    );

                  // Insertar la imagen en el slot adyacente
                  adjacentSlotGroup.appendChild(adjacentImgEl);

                  // Guardar la foto también en la página adyacente
                  if (!this.placedPhotos[adjacentPageKey])
                    this.placedPhotos[adjacentPageKey] = {};
                  this.placedPhotos[adjacentPageKey][slotIndex] = {
                    url: objectUrl,
                    fileIndex,
                    sourcePhotoId: photo.id || null,
                    photoId: !file && photo.id ? photo.id : null
                  };
                }
              }
            }
          }
        }
      }
    }

    // Actualizar conteo y estado del botón de impresión
    this.countFilledSlots();
    this.updateSendToPrintButtonState();
    this.updatePhotoSelectorUsedStatus();
  }

  selectBookSheet(pageNumber) {
    // Determinar qué hoja del libro se seleccionó (izquierda = 1, derecha = 2)
    const isLeftSheet = pageNumber === 1;
    const isRightSheet = pageNumber === 2;

    // Calcular la hoja real en el fotobook
    let actualPage;
    if (isLeftSheet) {
      actualPage = this.currentPage;
    } else if (isRightSheet) {
      actualPage = this.currentPage + 1;
    }

    // Verificar que la página existe
    if (actualPage > this.config.totalSheets) {
      return;
    }

    // Verificar si la página seleccionada está en formato de doble página
    const leftPageFormat = this.selectedFormats[this.currentPage];
    const rightPageFormat = this.selectedFormats[this.currentPage + 1];
    const isInDoublePageFormat =
      leftPageFormat &&
      rightPageFormat &&
      leftPageFormat.id === rightPageFormat.id &&
      leftPageFormat.double === true;

    // Si se está seleccionando una página que está en formato de doble página,
    // mostrar una advertencia o información
    if (isInDoublePageFormat) {
      // Opcional: mostrar un tooltip o indicador de que cambiar el formato romperá el doble página
    }

    this.selectedPage = actualPage;

    // Actualizar indicadores visuales de las páginas del libro
    this.updateBookPageIndicators();
  }

  // selectCoverSheet y updateCoverSheetIndicators eliminados
  // Las portadas no son seleccionables, solo editables

  updateBookPageIndicators() {
    // Obtener las hojas del libro
    const leftSheet = document.getElementById('left-sheet-preview');
    const rightSheet = document.getElementById('right-sheet-preview');

    if (!leftSheet || !rightSheet) {
      return;
    }

    // Obtener el contenedor de doble hoja (ahora es el contenedor padre)
    const bookPreviewContainer = document.querySelector('#sheets-container');

    // Remover indicadores de selección de todas las hojas
    leftSheet.classList.remove('selected', 'double-sheet');
    rightSheet.classList.remove('selected', 'double-sheet');

    // Ocultar todos los indicadores de selección
    const leftIndicator = leftSheet.querySelector('.sheet-selection-indicator');
    const rightIndicator = rightSheet.querySelector(
      '.sheet-selection-indicator'
    );
    const leftDoubleIndicator = leftSheet.querySelector(
      '.double-sheet-indicator'
    );
    const rightDoubleIndicator = rightSheet.querySelector(
      '.double-sheet-indicator'
    );

    if (leftIndicator) leftIndicator.style.opacity = '0';
    if (rightIndicator) rightIndicator.style.opacity = '0';
    if (leftDoubleIndicator) leftDoubleIndicator.style.opacity = '0';
    if (rightDoubleIndicator) rightDoubleIndicator.style.opacity = '0';

    // Determinar qué hoja del libro corresponde a la hoja seleccionada
    const isLeftSheetSelected = this.selectedPage === this.currentPage;
    const isRightSheetSelected = this.selectedPage === this.currentPage + 1;

    // Verificar si hay un formato de doble hoja aplicado
    const leftSheetFormat = this.selectedFormats[this.currentPage];
    const rightSheetFormat = this.selectedFormats[this.currentPage + 1];
    const isDoubleSheetFormat =
      leftSheetFormat &&
      rightSheetFormat &&
      leftSheetFormat.id === rightSheetFormat.id &&
      leftSheetFormat.double === true;

    // Aplicar modo de libro abierto al contenedor (siempre activo)
    if (bookPreviewContainer) {
      // Siempre mantener las hojas juntas para simular libro abierto
      bookPreviewContainer.classList.add('double-sheet-mode');
    }

    // Aplicar indicador visual
    if (isDoubleSheetFormat) {
      // Si es formato de doble hoja, seleccionar ambas hojas
      leftSheet.classList.add('selected', 'double-sheet');
      rightSheet.classList.add('selected', 'double-sheet');
      if (leftDoubleIndicator) leftDoubleIndicator.style.opacity = '1';
      if (rightDoubleIndicator) rightDoubleIndicator.style.opacity = '1';
    } else if (isLeftSheetSelected) {
      leftSheet.classList.add('selected');
      if (leftIndicator) leftIndicator.style.opacity = '1';
    } else if (isRightSheetSelected) {
      rightSheet.classList.add('selected');
      if (rightIndicator) rightIndicator.style.opacity = '1';
    }

    // Actualizar indicador de página seleccionada
    this.updatePageSelectionIndicator();
  }

  renderSheetPreviews() {
    document.querySelectorAll('.sheet-preview').forEach((container) => {
      const config = container.dataset.config;
      if (config) {
        try {
          const layoutData = this.parseLayoutData(config);
          if (layoutData) {
            // Para previews pequeños, mantener 64x64 pero escalar proporcionalmente
            const realDimensions = this.getRealPageDimensions();
            const scaleFactor = Math.min(
              64 / realDimensions.width,
              64 / realDimensions.height
            );
            const scaledWidth = realDimensions.width * scaleFactor;
            const scaledHeight = realDimensions.height * scaleFactor;
            const optionElement = container.closest('.sheet-format-option');
            const textConfig = optionElement
              ? this.buildCoverTextConfigFromDataset(optionElement.dataset)
              : null;
            const previewOptions = { showIndices: false, ...(textConfig ? { textConfig } : {}) };

            container.innerHTML = this.generateSVGPreview(
              layoutData,
              scaledWidth,
              scaledHeight,
              previewOptions,
              0
            );
          } else {
            container.innerHTML = this.generateDefaultSVG(64, 64);
          }
        } catch (e) {
          console.error('Error rendering sheet preview:', e);
          container.innerHTML = this.generateDefaultSVG(64, 64);
        }
      }
    });
  }

  initializeDefaultFormats() {
    const attemptInitialize = (attempt = 1) => {
      // Obtener todos los formatos disponibles
      const formatOptions = document.querySelectorAll('.sheet-format-option');

      if (formatOptions.length > 0) {
        // Convertir NodeList a Array para facilitar el acceso
        const formatsArray = Array.from(formatOptions);

        console.log('FotobookEditor: Initializing formats', {
          foundFormats: formatsArray.length,
          totalPages: this.config.totalSheets,
          attempt
        });

        // Asignar formatos cíclicamente a todas las páginas (no solo impares)
        for (let page = 1; page <= this.config.totalSheets; page++) {
          // Calcular el índice del formato usando módulo para rotación cíclica
          const formatIndex = (page - 1) % formatsArray.length;
          const selectedFormat = formatsArray[formatIndex];

          const formatInfo = {
            id: selectedFormat.dataset.sheetId,
            noPhotos: parseInt(selectedFormat.dataset.noPhotos, 10) || 0,
            fullPage: selectedFormat.dataset.fullPage === 'true',
            double: selectedFormat.dataset.double === 'true',
            config: selectedFormat.dataset.config,
            isCover: selectedFormat.dataset.isCover === 'true',
            textConfig: this.buildCoverTextConfigFromDataset(
              selectedFormat.dataset
            )
          };

          this.selectedFormats[page] = formatInfo;
        }

        // Calcular slots requeridos después de inicializar formatos
        this.totalRequiredSlots = this.calculateTotalRequiredSlots();

        console.log('FotobookEditor: Formats initialized successfully', {
          totalRequiredSlots: this.totalRequiredSlots,
          selectedFormatsCount: Object.keys(this.selectedFormats).length
        });

        // Seleccionar visualmente el primer formato
        const firstFormat = formatsArray[0];
        firstFormat.classList.add('selected');

        // Actualizar la vista previa de la página actual
        this.updateDoublePagePreview(this.selectedFormats[this.currentPage]);

        // Mostrar mensaje de confirmación
        this.showCyclicAssignmentMessage();

        // Actualizar estado del botón
        this.updateSendToPrintButtonState();
      } else {
        // Si no encuentra formatos, reintentar hasta 3 veces
        if (attempt < 3) {
          console.warn(`FotobookEditor: Sheet formats not found (attempt ${attempt}), retrying...`);
          setTimeout(() => attemptInitialize(attempt + 1), 300);
        } else {
          console.error('FotobookEditor: Failed to find sheet formats after 3 attempts');
        }
      }
    };

    // Esperar un poco para asegurar que todos los elementos estén renderizados, luego intentar inicializar
    setTimeout(() => attemptInitialize(), 100);
  }

  initializeDefaultCover() {
    // Esperar un momento para asegurar que todos los elementos estén renderizados
    setTimeout(() => {
      // Obtener todos los formatos de covers disponibles
      const coverFormatOptions = document.querySelectorAll(
        '.cover-format-option'
      );

      if (coverFormatOptions.length > 0) {
        // Seleccionar el primer cover disponible o el cover por defecto
        let defaultCoverOption = null;
        if (this.coverConfig && this.coverConfig.defaultCoverId) {
          defaultCoverOption = Array.from(coverFormatOptions).find(
            (opt) =>
              opt.dataset.coverId === String(this.coverConfig.defaultCoverId)
          );
        }

        // Si no se encuentra el cover por defecto, usar el primero
        if (!defaultCoverOption) {
          defaultCoverOption = coverFormatOptions[0];
        }

        // Seleccionar el cover por defecto
        if (defaultCoverOption) {
          this.selectCoverFormat(defaultCoverOption);
        }
      } else {
      }
    }, 150);
  }

  showCyclicAssignmentSummary() {
    const formatOptions = document.querySelectorAll('.sheet-format-option');
    const formatsArray = Array.from(formatOptions);

    if (formatsArray.length === 0) {
      return;
    }

    for (let page = 1; page <= this.config.totalSheets; page++) {
      const formatIndex = (page - 1) % formatsArray.length;
      const format = formatsArray[formatIndex];
      const selectedFormat = this.selectedFormats[page];

      if (selectedFormat) {
      } else {
      }
    }

    const pattern = [];
    for (
      let i = 0;
      i < Math.min(formatsArray.length, this.config.totalSheets);
      i++
    ) {
      const formatIndex = i % formatsArray.length;
      pattern.push(formatIndex + 1);
    }

    const formatCounts = {};
    for (let page = 1; page <= this.config.totalSheets; page++) {
      const formatIndex = (page - 1) % formatsArray.length;
      const formatName = `Formato ${formatIndex + 1}`;
      formatCounts[formatName] = (formatCounts[formatName] || 0) + 1;
    }

    Object.entries(formatCounts).forEach(([formatName, count]) => {});
  }

  showCyclicAssignmentMessage() {
    const formatOptions = document.querySelectorAll('.sheet-format-option');
    const formatsArray = Array.from(formatOptions);

    if (formatsArray.length === 0) return;

    // Crear o actualizar el mensaje de confirmación
    let messageEl = document.getElementById('cyclic-assignment-message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.id = 'cyclic-assignment-message';
      messageEl.className =
        'fixed top-4 left-4 bg-petrol-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transform transition-all duration-300';

      // Insertar después del header
      const header = document.querySelector('header');
      if (header) {
        header.parentNode.insertBefore(messageEl, header.nextSibling);
      } else {
        document.body.appendChild(messageEl);
      }
    }

    const totalPages = this.config.totalSheets;
    const totalFormats = formatsArray.length;
    const cycles = Math.floor(totalPages / 2 / totalFormats);
    const remaining = (totalPages / 2) % totalFormats;

    let messageText = `Formatos asignados cíclicamente: ${totalFormats} formatos → ${totalPages} páginas`;
    if (cycles > 0) {
      messageText += ` (${cycles} ciclos completos`;
      if (remaining > 0) {
        messageText += ` + ${remaining} páginas adicionales`;
      }
      messageText += ')';
    }

    messageEl.textContent = messageText;
    messageEl.style.transform = 'translateX(0)';
    messageEl.style.opacity = '1';

    // Ocultar el mensaje después de 5 segundos
    setTimeout(() => {
      messageEl.style.transform = 'translateX(-100%)';
      messageEl.style.opacity = '0';
    }, 5000);
  }

  getPageFormatInfo(page) {
    const formatOptions = document.querySelectorAll('.sheet-format-option');
    const formatsArray = Array.from(formatOptions);

    if (formatsArray.length === 0) return null;

    const formatIndex = ((page - 1) / 2) % formatsArray.length;
    const format = formatsArray[formatIndex];
    const selectedFormat = this.selectedFormats[page];

    return {
      page,
      formatIndex: formatIndex + 1,
      totalFormats: formatsArray.length,
      assignedFormat: selectedFormat,
      formatElement: format,
      cycleNumber: Math.floor((page - 1) / 2 / formatsArray.length) + 1,
      positionInCycle: (((page - 1) / 2) % formatsArray.length) + 1
    };
  }

  selectSheetFormat(option) {
    // Remover selección anterior
    document.querySelectorAll('.sheet-format-option').forEach((opt) => {
      opt.classList.remove('selected');
    });

    // Seleccionar nuevo formato
    option.classList.add('selected');

    // Actualizar información del formato seleccionado
    const formatInfo = {
      id: option.dataset.sheetId,
      noPhotos: parseInt(option.dataset.noPhotos, 10) || 0,
      fullPage: option.dataset.fullPage === 'true',
      double: option.dataset.double === 'true',
      config: option.dataset.config,
      isCover: option.dataset.isCover === 'true',
      textConfig: this.buildCoverTextConfigFromDataset(option.dataset)
    };

    // Determinar si es un formato de doble página
    const isDoublePageFormat = formatInfo.double === true;

    // Verificar si hay un formato de doble página activo en las páginas visibles
    const leftPageFormat = this.selectedFormats[this.currentPage];
    const rightPageFormat = this.selectedFormats[this.currentPage + 1];
    const hasActiveDoublePage =
      leftPageFormat &&
      rightPageFormat &&
      leftPageFormat.id === rightPageFormat.id &&
      leftPageFormat.double === true;

    // Determinar qué páginas aplicar el formato
    let pagesToApply = [];

    if (isDoublePageFormat) {
      // Si es formato de doble página, aplicar a ambas páginas visibles
      pagesToApply = [this.currentPage, this.currentPage + 1];
    } else {
      // Si es formato de página individual, aplicar solo a la página seleccionada
      pagesToApply = [this.selectedPage];

      // Si había un formato de doble página activo y se está aplicando un formato individual,
      // convertir la otra página a formato individual con página completa
      if (hasActiveDoublePage) {
        this.convertOtherPageToIndividual(this.selectedPage);
      }
    }

    // Aplicar el formato a las páginas correspondientes
    pagesToApply.forEach((page) => {
      if (page <= this.config.totalSheets) {
        this.selectedFormats[page] = formatInfo;
      }
    });

    // Actualizar vista previa de doble página
    // Usar el formato guardado de la página izquierda (después de aplicar el formato)
    // Si es formato de doble página o se aplicó a la página izquierda, usar formatInfo
    // Si no, leer de selectedFormats para mantener el formato existente de la izquierda
    const leftPageFormatForPreview =
      isDoublePageFormat || pagesToApply.includes(this.currentPage)
        ? formatInfo
        : this.selectedFormats[this.currentPage];
    this.updateDoublePagePreview(leftPageFormatForPreview);

    // Actualizar indicadores de las páginas del libro
    this.updateBookPageIndicators();

    // Actualizar tooltips de las páginas
    this.updatePageTooltips();

    // Recalcular slots requeridos después de cambiar formato
    this.totalRequiredSlots = null; // Forzar recálculo
    this.updateSendToPrintButtonState();
  }

  selectCoverFormat(option) {
    // Remover selección visual de todos los formatos de covers
    document.querySelectorAll('.cover-format-option').forEach((opt) => {
      opt.classList.remove('selected');
    });

    // Seleccionar visualmente el formato clickeado
    option.classList.add('selected');

    // Actualizar información del formato de portada seleccionado
    const coverFormatInfo = {
      id: option.dataset.coverId,
      noPhotos: parseInt(option.dataset.noPhotos, 10) || 0,
      config: option.dataset.config,
      coverTitle: option.dataset.coverTitle,
      coverSubtitle: option.dataset.coverSubtitle,
      coverTitleX: parseFloat(option.dataset.coverTitleX) || 0.5,
      coverTitleY: parseFloat(option.dataset.coverTitleY) || 0.3,
      coverTitleSize: parseFloat(option.dataset.coverTitleSize) || 0.1,
      coverSubtitleX: parseFloat(option.dataset.coverSubtitleX) || 0.5,
      coverSubtitleY: parseFloat(option.dataset.coverSubtitleY) || 0.4,
      coverSubtitleSize: parseFloat(option.dataset.coverSubtitleSize) || 0.05,
      fontFamilies: option.dataset.fontFamilies,
      palette: option.dataset.palette
    };

    this.selectedCoverFormat = coverFormatInfo;

    // No se actualizan indicadores de selección - las portadas no son seleccionables

    // Actualizar el CoverSelector si existe
    if (window.coverSelector && coverFormatInfo.id) {
      // Evitar bucle de recursión: no notificar de vuelta a FotobookEditor
      window.coverSelector.selectCover(coverFormatInfo.id, {
        notifyFotobookEditor: false
      });
    } else if (window.coverSelector) {
      // Si CoverSelector existe pero no se pudo seleccionar, actualizar manualmente
      const cover = window.COVER_CONFIG?.covers?.find(
        (c) => String(c.id) === String(coverFormatInfo.id)
      );
      if (cover) {
        window.coverSelector.updatePalette(cover);
        window.coverSelector.updateFonts(cover);
        window.coverSelector.updateCoverPreview(cover);
      }
    }

    // NO actualizar los inputs - mantener los valores del usuario
    // La previsualización se actualiza usando los valores de los inputs si existen,
    // o los valores por defecto del cover si los inputs están vacíos
  }

  updateCoverPreview(coverFormatInfo) {
    const coverSvg = document.getElementById('svg-cover-details');

    if (!coverSvg) {
      return;
    }

    // Renderizar usando el SVG completo (portada frontal está en cover-left-sheet-group)
    if (coverFormatInfo.config) {
      this.renderCoverSVG(coverSvg, coverFormatInfo, 'left');
    }

    // La contraportada NO se actualiza, solo mantiene el logo y el color de fondo
    // El color de fondo se actualiza desde CoverSelector.setBackgroundColor()
  }

  renderCoverSVG(svgElement, coverFormatInfo, side) {
    // Limpiar contenido previo excepto el rect de fondo
    const bgRect = svgElement.querySelector('rect');
    const existingContent = svgElement.querySelector('g.cover-sheet-content');
    if (existingContent) {
      existingContent.remove();
    }

    // Crear nuevo grupo de contenido
    const contentGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g'
    );
    contentGroup.setAttribute('class', 'cover-sheet-content');

    // Renderizar layout de fotos basado en config
    if (coverFormatInfo.config) {
      this.renderCoverLayout(contentGroup, coverFormatInfo.config, svgElement);
    }

    // Renderizar texto de portada
    this.renderCoverText(contentGroup, coverFormatInfo, side);

    svgElement.appendChild(contentGroup);
  }

  renderCoverLayout(contentGroup, config, svgElement) {
    // Parsear y renderizar el layout de fotos (similar a sheets pero para covers)
    if (!config) return;

    // Obtener las dimensiones de la portada individual (no del SVG completo)
    // El SVG completo incluye contraportada + lomo + portada, pero las posiciones
    // en el config son relativas a la portada individual
    const coverGroup = svgElement.querySelector('#cover-left-sheet-group');
    const coverRect = coverGroup?.querySelector(
      '#cover-left-sheet-background-rect'
    );
    if (!coverRect) {
      console.error(
        'FotobookEditor: No se encontró el rectángulo de la portada'
      );
      return;
    }

    // Las dimensiones de la portada individual (no del SVG completo)
    const coverWidth = parseFloat(coverRect.getAttribute('width'));
    const coverHeight = parseFloat(coverRect.getAttribute('height'));

    const shapes = config.split('|');
    shapes.forEach((shapeData, index) => {
      const shapeMatch = shapeData
        .trim()
        .match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/);
      if (!shapeMatch) return;

      const [, type, width, height, x, y] = shapeMatch;

      const slotGroup = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'g'
      );
      slotGroup.setAttribute('class', 'photo-slot cover-photo-slot');
      slotGroup.setAttribute('data-slot-index', index);

      const shapeElement =
        type === 'r'
          ? document.createElementNS('http://www.w3.org/2000/svg', 'rect')
          : document.createElementNS('http://www.w3.org/2000/svg', 'circle');

      if (type === 'r') {
        // Los valores en config son relativos (0-1), multiplicar por las dimensiones de la portada
        shapeElement.setAttribute('x', (parseFloat(x) * coverWidth).toFixed(2));
        shapeElement.setAttribute('y', (parseFloat(y) * coverHeight).toFixed(2));
        shapeElement.setAttribute('width', (parseFloat(width) * coverWidth).toFixed(2));
        shapeElement.setAttribute('height', (parseFloat(height) * coverHeight).toFixed(2));
      } else {
        // Para círculos, el width y height representan el diámetro
        const radius = (parseFloat(width) * coverWidth) / 2;
        shapeElement.setAttribute('r', radius.toFixed(2));
        shapeElement.setAttribute('cx', (parseFloat(x) * coverWidth + radius).toFixed(2));
        shapeElement.setAttribute('cy', (parseFloat(y) * coverHeight + radius).toFixed(2));
      }

      shapeElement.setAttribute('fill', 'none');
      shapeElement.setAttribute('stroke', '#55859a');
      shapeElement.setAttribute('stroke-width', '1');
      shapeElement.setAttribute('class', 'photo-slot-rect cover-photo-slot');
      shapeElement.setAttribute('data-slot-index', index);

      // Agregar imagen placeholder
      const placeholderUrl = `https://picsum.photos/${Math.round(parseFloat(width) * coverWidth)}/${Math.round(parseFloat(height) * coverHeight)}?random=${index}`;
      const imgElement = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      imgElement.setAttributeNS('http://www.w3.org/1999/xlink', 'href', placeholderUrl);
      imgElement.setAttribute('x', (parseFloat(x) * coverWidth).toFixed(2));
      imgElement.setAttribute('y', (parseFloat(y) * coverHeight).toFixed(2));
      imgElement.setAttribute('width', (parseFloat(width) * coverWidth).toFixed(2));
      imgElement.setAttribute('height', (parseFloat(height) * coverHeight).toFixed(2));
      imgElement.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      imgElement.setAttribute('opacity', '0.3');
      imgElement.setAttribute('class', 'photo-placeholder');

      if (type === 'c') {
        // Para círculos, agregar clip-path
        const clipId = `clip-circle-${index}`;
        let defs = svgElement.querySelector('defs');
        if (!defs) {
          defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
          svgElement.insertBefore(defs, svgElement.firstChild);
        }
        const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
        clipPath.setAttribute('id', clipId);
        const clipCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        const radius = (parseFloat(width) * coverWidth) / 2;
        clipCircle.setAttribute('cx', (parseFloat(x) * coverWidth + radius).toFixed(2));
        clipCircle.setAttribute('cy', (parseFloat(y) * coverHeight + radius).toFixed(2));
        clipCircle.setAttribute('r', radius.toFixed(2));
        clipPath.appendChild(clipCircle);
        defs.appendChild(clipPath);
        imgElement.setAttribute('clip-path', `url(#${clipId})`);
      }

      slotGroup.appendChild(imgElement);

      slotGroup.appendChild(shapeElement);
      contentGroup.appendChild(slotGroup);
    });
  }

  renderCoverText(contentGroup, coverFormatInfo, side) {
    const svgElement = contentGroup.closest('svg');
    if (!svgElement) return;

    // Obtener las dimensiones de la portada individual (no del SVG completo)
    const coverGroup = svgElement.querySelector('#cover-left-sheet-group');
    const coverRect = coverGroup?.querySelector(
      '#cover-left-sheet-background-rect'
    );
    if (!coverRect) {
      console.error(
        'FotobookEditor: No se encontró el rectángulo de la portada para renderCoverText'
      );
      return;
    }

    // Las dimensiones de la portada individual (no del SVG completo)
    const coverWidth = parseFloat(coverRect.getAttribute('width'));
    const coverHeight = parseFloat(coverRect.getAttribute('height'));

    // Obtener valores de alineación del cover desde COVER_CONFIG
    const cover = window.COVER_CONFIG?.covers?.find(
      (c) => String(c.id) === String(coverFormatInfo.id)
    );
    const titleAlign = cover?.title_align || 'center';
    const subtitleAlign = cover?.subtitle_align || 'center';

    // Calcular posición X según alineación
    const calculateTextX = (align, width) => {
      switch (align) {
        case 'left':
          return width * 0.1;
        case 'right':
          return width * 0.9;
        default: // center
          return width * 0.5;
      }
    };

    const getTextAnchor = (align) => {
      switch (align) {
        case 'left':
          return 'start';
        case 'right':
          return 'end';
        default: // center
          return 'middle';
      }
    };

    // Renderizar título
    if (coverFormatInfo.coverTitle) {
      const titleX = calculateTextX(titleAlign, coverWidth);
      const titleText = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      );
      titleText.setAttribute('x', titleX);
      titleText.setAttribute('y', coverFormatInfo.coverTitleY * coverHeight);
      titleText.setAttribute('text-anchor', getTextAnchor(titleAlign));
      titleText.setAttribute('dominant-baseline', 'middle');
      titleText.setAttribute(
        'font-size',
        coverFormatInfo.coverTitleSize * coverHeight
      );
      titleText.setAttribute('fill', side === 'left' ? '#FFF' : '#15803d');
      titleText.setAttribute('font-weight', 'bold');
      titleText.setAttribute('id', `cover-${side}-sheet-title-text`);
      if (coverFormatInfo.fontFamilies) {
        titleText.setAttribute(
          'font-family',
          `'${coverFormatInfo.fontFamilies.split(',')[0].trim()}', sans-serif`
        );
      }
      titleText.textContent = coverFormatInfo.coverTitle;
      contentGroup.appendChild(titleText);
    }

    // Renderizar subtítulo
    if (coverFormatInfo.coverSubtitle) {
      const subtitleX = calculateTextX(subtitleAlign, coverWidth);
      const subtitleText = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      );
      subtitleText.setAttribute('x', subtitleX);
      subtitleText.setAttribute(
        'y',
        coverFormatInfo.coverSubtitleY * coverHeight
      );
      subtitleText.setAttribute('text-anchor', getTextAnchor(subtitleAlign));
      subtitleText.setAttribute('dominant-baseline', 'middle');
      subtitleText.setAttribute(
        'font-size',
        coverFormatInfo.coverSubtitleSize * coverHeight
      );
      subtitleText.setAttribute('fill', '#374151');
      subtitleText.setAttribute('id', `cover-${side}-sheet-subtitle-text`);
      if (coverFormatInfo.fontFamilies) {
        subtitleText.setAttribute(
          'font-family',
          `'${coverFormatInfo.fontFamilies.split(',')[0].trim()}', sans-serif`
        );
      }
      subtitleText.textContent = coverFormatInfo.coverSubtitle;
      contentGroup.appendChild(subtitleText);
    }
  }

  convertOtherPageToIndividual(selectedPage) {
    // Determinar cuál es la otra página (la que no fue seleccionada)
    const otherPage =
      selectedPage === this.currentPage
        ? this.currentPage + 1
        : this.currentPage;

    // Verificar que la otra página existe
    if (otherPage > this.config.totalSheets) {
      return;
    }

    // Buscar un formato de página completa individual
    const formatOptions = document.querySelectorAll('.sheet-format-option');
    let individualFullPageFormat = null;

    for (let option of formatOptions) {
      const isDouble = option.dataset.double === 'true';
      const isFullPage = option.dataset.fullPage === 'true';

      // Buscar un formato que sea de página completa pero no de doble página
      if (!isDouble && isFullPage) {
        individualFullPageFormat = {
          id: option.dataset.sheetId,
          noPhotos: parseInt(option.dataset.noPhotos, 10) || 0,
          fullPage: true,
          double: false,
          config: option.dataset.config,
          isCover: option.dataset.isCover === 'true',
          textConfig: this.buildCoverTextConfigFromDataset(option.dataset)
        };
        break;
      }
    }

    // Si no se encuentra un formato de página completa, usar el primer formato individual disponible
    if (!individualFullPageFormat) {
      for (let option of formatOptions) {
        const isDouble = option.dataset.double === 'true';
        if (!isDouble) {
          individualFullPageFormat = {
            id: option.dataset.sheetId,
            noPhotos: parseInt(option.dataset.noPhotos, 10) || 0,
            fullPage: option.dataset.fullPage === 'true',
            double: false,
            config: option.dataset.config,
            isCover: option.dataset.isCover === 'true',
            textConfig: this.buildCoverTextConfigFromDataset(option.dataset)
          };
          break;
        }
      }
    }

    // Aplicar el formato individual a la otra página
    if (individualFullPageFormat) {
      this.selectedFormats[otherPage] = individualFullPageFormat;

      // Recalcular slots requeridos después de convertir página
      this.totalRequiredSlots = null; // Forzar recálculo
      this.updateSendToPrintButtonState();
    } else {
    }
  }

  updateDoublePagePreview(formatInfo) {
    const leftPreview = document.getElementById('left-sheet-preview');
    const rightPreview = document.getElementById('right-sheet-preview');

    if (!leftPreview || !rightPreview) {
      // Silenciar el error si estamos en la vista de selección de portadas
      // donde estos elementos no existen
      if (!document.getElementById('cover-sheets-container')) {
        console.error('FotobookEditor: Left or right preview not found');
      }
      return;
    }

    // Obtener el SVG completo, no solo el grupo page-content
    const leftSvg = leftPreview.querySelector('svg');
    const rightSvg = rightPreview.querySelector('svg');

    if (!leftSvg || !rightSvg) {
      // Silenciar el error si estamos en la vista de selección de portadas
      if (!document.getElementById('cover-sheets-container')) {
        console.error('FotobookEditor: Left or right SVG not found');
      }
      return;
    }

    [leftPreview, rightPreview].forEach((preview) => {
      preview.style.width = '';
      preview.style.height = '';
      preview.style.maxWidth = '';
      preview.style.maxHeight = '';
      preview.style.position = '';
      preview.style.left = '';
      preview.style.right = '';
      preview.style.top = '';
      preview.style.bottom = '';
      preview.style.flex = '';
    });

    // Verificar si la página derecha existe
    // En un álbum, las páginas siempre vienen en pares (izquierda y derecha)
    // La página derecha existe si no estamos en la última página impar
    const rightPageNumber = this.currentPage + 1;
    const totalSheets = this.config.totalSheets || 0;
    const rightPageExists = rightPageNumber <= totalSheets;

    // Obtener formato para la página izquierda (página actual)
    const leftPageFormat = formatInfo;

    // Obtener formato para la página derecha (si existe)
    const rightPageFormat = rightPageExists
      ? this.selectedFormats[rightPageNumber]
      : null;

    // Verificar si la página izquierda es la última hoja (reservada para colofón)
    const isLeftPageLast = this.currentPage === this.config.totalSheets;
    
    // Renderizar página izquierda
    if (isLeftPageLast) {
      // La última hoja muestra el colofón, no slots de fotos
      leftSvg.outerHTML = this.generateColophonSVG();
    } else if (leftPageFormat && leftPageFormat.config) {
      try {
        const leftLayoutData = this.parseLayoutData(leftPageFormat.config);
        if (leftLayoutData) {
          const leftPreviewOptions = { showIndices: true, ...(leftPageFormat && leftPageFormat.textConfig ? { textConfig: leftPageFormat.textConfig } : {}) };
          const leftStartIndex = this.getTotalSlotsBeforePage(this.currentPage);
          const generatedSVG = this.generateSVGPreview(
            leftLayoutData,
            undefined,
            undefined,
            leftPreviewOptions,
            leftStartIndex
          );
          leftSvg.outerHTML = generatedSVG;
        } else {
          leftSvg.outerHTML = this.generateDefaultSVG();
        }
      } catch (e) {
        console.error('Error parsing left page layout data:', e);
        leftSvg.outerHTML = this.generateDefaultSVG();
      }
    } else {
      leftSvg.outerHTML = this.generateDefaultSVG();
    }

    // Reaplicar fotos colocadas en la página izquierda (solo si no es la última hoja)
    if (!isLeftPageLast) {
      const newLeftSvg = leftPreview.querySelector('svg');
      if (newLeftSvg) {
        this.applyPlacedPhotosToPage(newLeftSvg, this.currentPage);
      }
    }

    // Verificar si la página derecha es la última hoja (reservada para colofón)
    const isRightPageLast = rightPageNumber === this.config.totalSheets;

    // Renderizar página derecha
    if (rightPageExists) {
      if (isRightPageLast) {
        // La última hoja muestra el colofón, no slots de fotos
        rightSvg.outerHTML = this.generateColophonSVG();
      } else if (rightPageFormat && rightPageFormat.config) {
        try {
          const rightLayoutData = this.parseLayoutData(rightPageFormat.config);
          if (rightLayoutData) {
            const rightPreviewOptions = { showIndices: true, ...(rightPageFormat && rightPageFormat.textConfig ? { textConfig: rightPageFormat.textConfig } : {}) };
            const rightStartIndex = this.getTotalSlotsBeforePage(this.currentPage + 1);
            rightSvg.outerHTML = this.generateSVGPreview(
              rightLayoutData,
              undefined,
              undefined,
              rightPreviewOptions,
              rightStartIndex
            );
          } else {
            rightSvg.outerHTML = this.generateDefaultSVG();
          }
        } catch (e) {
          console.error('Error parsing right page layout data:', e);
          rightSvg.outerHTML = this.generateDefaultSVG();
        }
      } else {
        rightSvg.outerHTML = this.generateDefaultSVG();
      }

      // Reaplicar fotos colocadas en la página derecha (solo si no es la última hoja)
      if (!isRightPageLast) {
        const newRightSvg = rightPreview.querySelector('svg');
        if (newRightSvg) {
          this.applyPlacedPhotosToPage(newRightSvg, rightPageNumber);
        }
      }
    } else {
      // Si no existe la página derecha, mostrar un SVG vacío en lugar de HTML
      // Esto solo debería ocurrir cuando el número total de páginas es impar
      // y estamos en la última página
      rightSvg.outerHTML = this.generateDefaultSVG();
    }
  }

  applyPlacedPhotosToPage(svg, pageNumber) {
    if (!svg) return;
    const placedForPage = this.placedPhotos[pageNumber];
    if (!placedForPage) return;

    Object.entries(placedForPage).forEach(([slotIndex, data]) => {
      const slotGroup =
        svg.querySelector(`g.photo-slot[data-slot-index="${slotIndex}"]`) ||
        svg.querySelector(`.cover-photo-slot[data-slot-index="${slotIndex}"]`);
      const slotRect =
        svg.querySelector(`.photo-slot-rect[data-slot-index="${slotIndex}"]`) ||
        svg.querySelector(`.cover-photo-slot[data-slot-index="${slotIndex}"]`);
      if (!slotGroup || !slotRect) return;

      const { x, y, w, h } = this.getSlotBox(slotRect);
      if ([x, y, w, h].some((v) => Number.isNaN(v))) {
        console.warn('FotobookEditor: slot dimensions invalid', {
          slotIndex,
          slotRect
        });
        return;
      }

      // Eliminar imagen previa si existe para evitar duplicados
      const prevImg = slotGroup.querySelector('image');
      if (prevImg) prevImg.remove();

      const pageKey = this.resolvePageKey(
        svg.closest('.sheet-content') || svg.closest('.cover-sheet-container')
      );
      const clipId = this.createClipPathForSlot(
        svg,
        slotRect,
        pageKey,
        slotIndex
      );

      const imgEl = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'image'
      );
      imgEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', data.url);
      imgEl.setAttribute('x', String(x));
      imgEl.setAttribute('y', String(y));
      imgEl.setAttribute('width', String(w));
      imgEl.setAttribute('height', String(h));
      imgEl.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      imgEl.setAttribute('data-file-index', String(data.fileIndex));
      if (clipId) imgEl.setAttribute('clip-path', `url(#${clipId})`);
      slotGroup.appendChild(imgEl);
    });

    // En portadas, asegurar que los textos queden sobre las fotos
    if (svg.id === 'svg-cover-details') {
      this.bringCoverTextToFront(svg);
    }
  }

  showDefaultDoublePage(leftContent, rightContent, rightPageExists) {
    leftContent.innerHTML = this.generateDefaultSVG();
    if (rightPageExists) {
      rightContent.innerHTML = this.generateDefaultSVG();
    } else {
      rightContent.innerHTML = this.generateDefaultSVG();
    }
  }

  showErrorDoublePage(leftContent, rightContent) {
    leftContent.innerHTML = this.generateDefaultSVG();
    rightContent.innerHTML = this.generateDefaultSVG();
  }

  generateSVGPreview(layoutData, width, height, options = {}, startIndex = 0) {
    if (!Array.isArray(layoutData)) {
      return this.generateDefaultSVG(width, height);
    }

    // Usar dimensiones reales de página si no se especifican
    const realDimensions = this.getRealPageDimensions();
    const actualWidth = width || realDimensions.width;
    const actualHeight = height || realDimensions.height;

    // Debug: verificar cálculos de posición
    this.debugPositionCalculation(layoutData, actualWidth, actualHeight);

    // Validar y ajustar el formato si es necesario
    const adjustedLayoutData = this.validateAndAdjustFormat(
      layoutData,
      actualWidth,
      actualHeight
    );

    const photoElements = adjustedLayoutData
      .map((photo, index) => {
        // Los valores x, y están en el centro de la foto, no en la esquina
        // Necesitamos calcular la esquina superior izquierda
        const photoWidth = (photo.width * actualWidth) / 100;
        const photoHeight = (photo.height * actualHeight) / 100;
        const centerX = (photo.x * actualWidth) / 100;
        const centerY = (photo.y * actualHeight) / 100;

        // Calcular la esquina superior izquierda desde el centro
        const x = centerX - photoWidth / 2;
        const y = centerY - photoHeight / 2;
        const fontSize = actualHeight * 0.025;

        const textElement = (options.showIndices !== false) ? `<text x="${(x + photoWidth - fontSize * 0.5).toFixed(2)}" y="${(y + fontSize).toFixed(2)}" font-size="${fontSize.toFixed(2)}" fill="black" text-anchor="end">${startIndex + index + 1}</text>` : '';

        return `
        <g class="photo-slot" data-slot-index="${index}">
          <rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${photoWidth.toFixed(2)}" height="${photoHeight.toFixed(2)}"
                class="photo-slot-rect" data-slot-index="${index}" rx="2"/>
          ${textElement}
        </g>
      `;
      })
      .join('');

    const textConfig =
      options && options.textConfig ? options.textConfig : null;
    const coverTextElements = this.generateCoverTextElements(
      textConfig,
      actualWidth,
      actualHeight
    );

    return `
      <svg class="w-full h-full" viewBox="0 0 ${actualWidth} ${actualHeight}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#ffffff" stroke="none" stroke-width="1"/>
        ${photoElements}
        ${coverTextElements}
      </svg>
    `;
  }

  generateDefaultSVG(width, height) {
    // Usar dimensiones reales de página si no se especifican
    const realDimensions = this.getRealPageDimensions();
    const actualWidth = width || realDimensions.width;
    const actualHeight = height || realDimensions.height;

    return `
      <svg class="w-full h-full" viewBox="0 0 ${actualWidth} ${actualHeight}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#ffffff" stroke="#55859a" stroke-width="1"/>
      </svg>
    `;
  }

  generateColophonSVG(width, height) {
    // Usar dimensiones reales de página si no se especifican
    const realDimensions = this.getRealPageDimensions();
    const actualWidth = width || realDimensions.width;
    const actualHeight = height || realDimensions.height;

    // Generar texto del colofón
    const paperName = this.config.paperName || 'Papel estándar';
    const fontFamilies = this.getFontFamiliesFromCover() || 'Arial';
    
    const colophonText = this.colophonService.generate({
      createdAt: new Date(),
      tipoPapel: paperName,
      tipografias: fontFamilies,
    });

    // Dividir el texto en líneas (máximo 65 caracteres para no desbordar)
    const lines = this.wrapText(colophonText, 65);
    // Tamaño de fuente fijo: 10pt = ~42px a 300 DPI (estándar para todos los productos)
    const fontSize = 42; // 10pt fijo
    const lineHeight = fontSize * 1.4; // Interlineado estándar
    const padding = actualWidth * 0.08;
    // Posicionar al fondo con padding
    const startY = actualHeight - (padding) - (lines.length * lineHeight);

    // Generar elementos de texto para cada línea, alineados a la izquierda
    const textElements = lines.map((line, index) => {
      const y = startY + (index * lineHeight);
      return `<text x="${padding}" y="${y}" font-size="${fontSize}" fill="#323232" text-anchor="start" font-family="Arial, sans-serif">${this.escapeXml(line)}</text>`;
    }).join('');

    return `
      <svg class="w-full h-full" viewBox="0 0 ${actualWidth} ${actualHeight}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#ffffff" stroke="none"/>
        ${textElements}
      </svg>
    `;
  }

  wrapText(text, maxLength) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  escapeXml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  getFontFamiliesFromCover() {
    // Obtener la tipografía seleccionada actualmente en el selector de portada
    const fontSelect = document.getElementById('cover-typography');
    if (fontSelect && fontSelect.value) {
      return fontSelect.value;
    }
    return 'Arial';
  }

  generateCoverTextElements(textConfig, pageWidth, pageHeight) {
    if (!textConfig || textConfig.isCover !== true) {
      return '';
    }

    const elements = [];

    // Verificar que title existe y tiene contenido
    if (textConfig.title) {
      const titleElement = this.buildSVGTextElement(
        textConfig.title,
        {
          fill: '#1f2937',
          fontWeight: 'bold'
        },
        pageWidth,
        pageHeight
      );

      if (titleElement && titleElement.trim() !== '') {
        elements.push(titleElement);
      }
    } else {
    }

    // Verificar que subtitle existe y tiene contenido
    if (textConfig.subtitle) {
      const subtitleElement = this.buildSVGTextElement(
        textConfig.subtitle,
        {
          fill: '#FFFFFF'
        },
        pageWidth,
        pageHeight
      );

      if (subtitleElement && subtitleElement.trim() !== '') {
        elements.push(subtitleElement);
      }
    }

    if (elements.length === 0) {
      return '';
    }

    // El grupo de texto debe renderizarse DESPUÉS de las fotos para estar encima
    const result = `
      <g class="cover-text-layer" style="pointer-events: none;">
        ${elements.join('\n        ')}
      </g>
    `;
    return result;
  }

  resolvePageKey(pageEl) {
    if (!pageEl || !pageEl.id) return this.currentPage;
    if (pageEl.id.includes('cover')) {
      return 'cover-front';
    }
    const isLeftSheet = pageEl.id === 'left-sheet-preview';
    return isLeftSheet ? this.currentPage : this.currentPage + 1;
  }

  createClipPathForSlot(svg, slotRect, pageKey, slotIndex) {
    if (!svg || !slotRect) return null;
    const defs =
      svg.querySelector('defs') ||
      (() => {
        const d = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'defs'
        );
        svg.insertBefore(d, svg.firstChild);
        return d;
      })();

    const clipId = `clip-slot-${pageKey}-${slotIndex}`;
    let clipPath = defs.querySelector(`#${clipId}`);
    if (!clipPath) {
      clipPath = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'clipPath'
      );
      clipPath.setAttribute('id', clipId);
      clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
      defs.appendChild(clipPath);
    } else {
      // Limpia contenido previo
      while (clipPath.firstChild) clipPath.removeChild(clipPath.firstChild);
      clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    }

    if (slotRect.tagName === 'rect') {
      const clone = slotRect.cloneNode(false);
      clone.removeAttribute('class');
      clone.removeAttribute('data-slot-index');
      clipPath.appendChild(clone);
    } else if (slotRect.tagName === 'circle') {
      const clone = slotRect.cloneNode(false);
      clone.removeAttribute('class');
      clone.removeAttribute('data-slot-index');
      clipPath.appendChild(clone);
    }

    return clipId;
  }

  getSlotBox(slotEl) {
    if (!slotEl) return { x: NaN, y: NaN, w: NaN, h: NaN };
    if (slotEl.tagName === 'rect') {
      const x = parseFloat(slotEl.getAttribute('x'));
      const y = parseFloat(slotEl.getAttribute('y'));
      const w = parseFloat(slotEl.getAttribute('width'));
      const h = parseFloat(slotEl.getAttribute('height'));
      return { x, y, w, h };
    }
    if (slotEl.tagName === 'circle') {
      const r = parseFloat(slotEl.getAttribute('r'));
      const cx = parseFloat(slotEl.getAttribute('cx'));
      const cy = parseFloat(slotEl.getAttribute('cy'));
      if ([r, cx, cy].some((v) => Number.isNaN(v)))
        return { x: NaN, y: NaN, w: NaN, h: NaN };
      return { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
    }
    return { x: NaN, y: NaN, w: NaN, h: NaN };
  }

  bringCoverTextToFront(svg) {
    if (!svg) return;
    const contentGroup = svg.querySelector('#cover-content-group') || svg;

    // Reinsertar grupos de texto al final para que queden al tope del z-index SVG
    ['cover-left-sheet-title-group', 'cover-left-sheet-subtitle-group'].forEach(
      (id) => {
        const group = svg.querySelector(`#${id}`);
        if (group && group.parentNode) {
          group.parentNode.removeChild(group);
          contentGroup.appendChild(group);
        }
      }
    );
  }

  buildSVGTextElement(config, style, pageWidth, pageHeight) {
    if (!config) {
      return '';
    }

    const x = this.clampRelativeValue(config.x, 0, 1) * pageWidth;
    const y = this.clampRelativeValue(config.y, 0, 1) * pageHeight;
    const baseSize = Math.min(pageWidth, pageHeight);
    let fontSize = this.ensurePositiveSize(config.size) * baseSize;

    // Asegurar un tamaño mínimo de fuente para visibilidad
    const minFontSize = Math.max(12, baseSize * 0.03); // Mínimo 12px o 3% del tamaño base
    fontSize = Math.max(fontSize, minFontSize);

    const textContent = this.escapeSVGText(config.text || '');
    const fontFamily =
      style && style.fontFamily ? style.fontFamily : 'Arial, sans-serif';
    const fill = style && style.fill ? style.fill : '#1f2937';
    const fontWeight = style && style.fontWeight ? style.fontWeight : 'normal';

    if (!textContent || textContent.trim() === '') {
      return '';
    }

    // Asegurar que el texto sea visible con un stroke sutil para contraste
    const strokeWidth = Math.max(1, fontSize * 0.02);

    return `
      <text x="${x.toFixed(2)}" y="${y.toFixed(2)}" text-anchor="middle" dominant-baseline="middle"
            font-size="${fontSize.toFixed(2)}" fill="${fill}" font-family="${fontFamily}" font-weight="${fontWeight}"
            stroke="rgba(255, 255, 255, 0.8)" stroke-width="${strokeWidth.toFixed(2)}" stroke-linejoin="round"
            paint-order="stroke fill">
        ${textContent}
      </text>
    `;
  }

  clampRelativeValue(value, min = 0, max = 1) {
    if (!Number.isFinite(value)) {
      return Math.min(Math.max(0.5, min), max);
    }
    return Math.min(Math.max(value, min), max);
  }

  ensurePositiveSize(value) {
    if (!Number.isFinite(value) || value <= 0) {
      return 0.06;
    }
    return value;
  }

  buildCoverTextConfigFromDataset(dataset) {
    if (!dataset || dataset.isCover !== 'true') {
      return null;
    }

    // Los atributos data-* se convierten a camelCase en dataset
    // data-cover-title -> dataset.coverTitle
    // data-cover-title-x -> dataset.coverTitleX
    const titleText =
      dataset.coverTitle && dataset.coverTitle.trim() !== ''
        ? dataset.coverTitle
        : 'Keep the moment';
    const titleX = dataset.coverTitleX ? parseFloat(dataset.coverTitleX) : 0.5;
    const titleY = dataset.coverTitleY ? parseFloat(dataset.coverTitleY) : 0.15;
    const titleSize = dataset.coverTitleSize
      ? parseFloat(dataset.coverTitleSize)
      : 0.08;

    const subtitleText =
      dataset.coverSubtitle && dataset.coverSubtitle.trim() !== ''
        ? dataset.coverSubtitle
        : 'Print it';
    const subtitleX = dataset.coverSubtitleX
      ? parseFloat(dataset.coverSubtitleX)
      : 0.5;
    const subtitleY = dataset.coverSubtitleY
      ? parseFloat(dataset.coverSubtitleY)
      : 0.85;
    const subtitleSize = dataset.coverSubtitleSize
      ? parseFloat(dataset.coverSubtitleSize)
      : 0.06;

    return {
      isCover: true,
      title: this.buildTextLayerConfig(
        {
          text: titleText,
          x: titleX,
          y: titleY,
          size: titleSize
        },
        { text: 'Keep the moment', x: 0.5, y: 0.15, size: 0.08 }
      ),
      subtitle: this.buildTextLayerConfig(
        {
          text: subtitleText,
          x: subtitleX,
          y: subtitleY,
          size: subtitleSize
        },
        { text: 'Print it', x: 0.5, y: 0.85, size: 0.06 }
      )
    };
  }

  buildTextLayerConfig(raw = {}, defaults) {
    const textValue = raw.text != null ? String(raw.text) : '';
    const sanitizedText = textValue.trim();

    return {
      text: sanitizedText || defaults.text,
      x: this.parseRelativePosition(raw.x, defaults.x),
      y: this.parseRelativePosition(raw.y, defaults.y),
      size: this.parseRelativeSize(raw.size, defaults.size)
    };
  }

  parseRelativePosition(value, fallback) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : fallback;
  }

  parseRelativeSize(value, fallback) {
    const num = parseFloat(value);
    if (!Number.isFinite(num) || num <= 0) {
      return fallback;
    }
    return num;
  }

  escapeSVGText(content) {
    if (content === null || content === undefined) {
      return '';
    }

    return String(content)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  parseLayoutData(config) {
    if (!config || config.trim() === '') {
      return null;
    }

    // Intentar parsear como JSON primero
    try {
      const data = JSON.parse(config);
      return this.normalizeLayoutData(data);
    } catch (e) {
      // Ignorar y seguir probando otros formatos
    }

    // Compatibilidad con el formato de layouts usado en administración:
    // "r:0.5,0.5,0.25,0.25|c:0.3,0.3,0.1,0.1"
    const shapeLayout = this.parseShapeLayoutData(config);
    if (shapeLayout) {
      return shapeLayout;
    }

    // Formato heredado basado en porcentajes centrados: ".3,.3,0,.17|..."
    return this.parseCommaSeparatedData(config);
  }

  parseShapeLayoutData(config) {
    if (!config || typeof config !== 'string') return null;

    const shapes = config
      .split('|')
      .map((shape) => shape.trim())
      .filter(Boolean);
    if (shapes.length === 0) return null;

    const parsed = shapes
      .map((shapeStr) => this.normalizeShapeString(shapeStr))
      .filter(Boolean);
    return parsed.length > 0 ? parsed : null;
  }

  normalizeShapeString(shapeStr) {
    if (!shapeStr) return null;

    // Detectar y convertir formato antiguo "r:w:0.5,h:0.5,x:0.25,y:0.25"
    const oldFormatRegex =
      /^[rc]:w?:[\d.]+,\s*h?:[\d.]+,\s*x?:[\d.]+,\s*y?:[\d.]+/;
    if (oldFormatRegex.test(shapeStr)) {
      shapeStr = this.convertOldShapeFormat(shapeStr);
    }

    const parts = shapeStr.split(':');
    if (parts.length !== 2) return null;

    const coords = parts[1].split(',').map((n) => parseFloat(n.trim()));
    if (coords.length !== 4 || coords.some(Number.isNaN)) return null;

    const [width, height, top, left] = coords;
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      !Number.isFinite(top) ||
      !Number.isFinite(left)
    ) {
      return null;
    }

    // El formato está en valores relativos (0-1 para una página, 0-2 para doble página).
    // Convertimos a porcentajes y a sistema basado en el centro (usado por generateSVGPreview).
    return {
      type: (parts[0] || '').trim().toLowerCase(),
      width: width * 100,
      height: height * 100,
      x: (left + width / 2) * 100,
      y: (top + height / 2) * 100
    };
  }

  convertOldShapeFormat(oldData) {
    const shapeType = oldData[0] || 'r';
    const values = oldData.match(/[\d.]+/g);
    if (!values || values.length < 4) return oldData;
    const [w, h, x, y] = values;
    return `${shapeType}:${w},${h},${x},${y}`;
  }

  parseCommaSeparatedData(config) {
    // Formato: "0.8,0.8,0.5,0.25" -> [{"width": 80, "height": 80, "x": 50, "y": 25}]
    // Formato múltiple: ".3,.3,0,.17|.3,.3,.47,.17|.3,.3,.94,.17" -> Array de fotos
    // Los valores representan: width, height, x_center, y_center

    // Verificar si hay múltiples fotos (separadas por |)
    if (config.includes('|')) {
      const photoConfigs = config.split('|');
      const photos = [];

      photoConfigs.forEach((photoConfig, index) => {
        const numbers = photoConfig.split(',').map((n) => parseFloat(n.trim()));
        if (numbers.length >= 4 && !numbers.some(isNaN)) {
          photos.push({
            width: numbers[0] * 100, // 0.3 -> 30% del ancho
            height: numbers[1] * 100, // 0.3 -> 30% del alto
            x: numbers[2] * 100, // 0 -> 0% (esquina izquierda)
            y: numbers[3] * 100 // 0.17 -> 17% (centro vertical)
          });
        } else {
          console.warn(
            `FotobookEditor: Invalid photo config at index ${index}:`,
            photoConfig
          );
        }
      });

      return photos.length > 0 ? photos : null;
    } else {
      // Formato de una sola foto (código original)
      const numbers = config.split(',').map((n) => parseFloat(n.trim()));
      if (numbers.length < 4 || numbers.some(isNaN)) {
        return null;
      }

      // Los valores están en escala 0-1, convertirlos a porcentajes
      // width, height, x_center, y_center
      return [
        {
          width: numbers[0] * 100, // 0.8 -> 80% del ancho
          height: numbers[1] * 100, // 0.8 -> 80% del alto
          x: numbers[2] * 100, // 0.5 -> 50% (centro horizontal)
          y: numbers[3] * 100 // 0.25 -> 25% (centro vertical)
        }
      ];
    }
  }

  normalizeLayoutData(data) {
    if (!Array.isArray(data)) {
      return null;
    }

    return data
      .map((photo) => {
        if (!photo || typeof photo !== 'object') {
          return null;
        }

        // Normalizar nombres de propiedades
        const normalized = {};
        normalized.width = photo.width || photo.w || 50;
        normalized.height = photo.height || photo.h || 50;
        normalized.x = photo.x || 25; // Centro horizontal por defecto
        normalized.y = photo.y || 25; // Centro vertical por defecto

        // Convertir a porcentajes si están en formato decimal (0-1)
        if (normalized.width <= 1) {
          normalized.width *= 100;
          normalized.height *= 100;
          normalized.x *= 100;
          normalized.y *= 100;
        }

        // Asegurar que x, y representen el centro de la foto
        // Los valores ya están en el centro, no necesitan ajuste adicional

        return normalized;
      })
      .filter(Boolean);
  }

  debugPositionCalculation(layoutData, width, height) {

    if (Array.isArray(layoutData)) {
      layoutData.forEach((photo, index) => {
        const photoWidth = (photo.width * width) / 100;
        const photoHeight = (photo.height * height) / 100;
        const centerX = (photo.x * width) / 100;
        const centerY = (photo.y * height) / 100;
        const x = centerX - photoWidth / 2;
        const y = centerY - photoHeight / 2;
      });
    }
  }

  testPositionCalculation() {

    // Ejemplo: "0.8,0.8,0.5,0.25" -> width: 80%, height: 80%, x: 50%, y: 25%
    const testConfig = '0.8,0.8,0.5,0.25';
    const layoutData = this.parseLayoutData(testConfig);
    const width = 100; // Cambiar a 100x100 para mejor visualización
    const height = 100;


    if (layoutData) {
      this.debugPositionCalculation(layoutData, width, height);

      // Verificar que la foto esté centrada correctamente
      const photo = layoutData[0];
      const photoWidth = (photo.width * width) / 100; // 80% de 100 = 80px
      const photoHeight = (photo.height * height) / 100; // 80% de 100 = 80px
      const centerX = (photo.x * width) / 100; // 50% de 100 = 50px
      const centerY = (photo.y * height) / 100; // 25% de 100 = 25px
      const x = centerX - photoWidth / 2; // 50 - 40 = 10px
      const y = centerY - photoHeight / 2; // 25 - 40 = -15px

      // Verificar si la foto cabe en la página
      const fitsInPage =
        x >= 0 &&
        y >= 0 &&
        x + photoWidth <= width &&
        y + photoHeight <= height;
      
      if (!fitsInPage) {
        console.log('PROBLEMA: La foto sale de los límites de la página');
        console.log('Límites de la página: 0,0 a 100,100');
        console.log(
          'Límites de la foto: ' +
            x +
            ',' +
            y +
            ' a ' +
            (x + photoWidth) +
            ',' +
            (y + photoHeight)
        );
      }

      console.log('Expected calculations:', {
        photoWidth: 80,
        photoHeight: 80,
        centerX: 50,
        centerY: 25,
        x: 10,
        y: -15
      });

      console.log('Actual calculations:', {
        photoWidth,
        photoHeight,
        centerX,
        centerY,
        x,
        y
      });
    }
  }

  testMultiplePhotosFormat() {
    console.log('FotobookEditor: Testing multiple photos format');

    // Ejemplo del formato proporcionado
    const testConfig =
      '.3,.3,0,.17|.3,.3,.47,.17|.3,.3,.94,.17|.3,.3,.06,.83|.3,.3,.53,.83|.3,.3,1,.83';
    const layoutData = this.parseLayoutData(testConfig);
    const width = 100;
    const height = 100;

    console.log('Test config (multiple photos):', testConfig);
    console.log('Parsed layout data:', layoutData);

    if (layoutData && Array.isArray(layoutData)) {
      console.log(
        `\n=== ANÁLISIS DE ${layoutData.length} FOTOS EN HOJA 100x100px ===`
      );

      layoutData.forEach((photo, index) => {
        const photoWidth = (photo.width * width) / 100;
        const photoHeight = (photo.height * height) / 100;
        const centerX = (photo.x * width) / 100;
        const centerY = (photo.y * height) / 100;
        const x = centerX - photoWidth / 2;
        const y = centerY - photoHeight / 2;

        const fitsInPage =
          x >= 0 &&
          y >= 0 &&
          x + photoWidth <= width &&
          y + photoHeight <= height;

        console.log(`\n📷 Foto ${index + 1}:`);
        console.log(
          `   Tamaño: ${photoWidth.toFixed(1)}x${photoHeight.toFixed(1)}px (${
            photo.width
          }%x${photo.height}%)`
        );
        console.log(
          `   Centro: (${centerX.toFixed(1)}, ${centerY.toFixed(1)}) (${
            photo.x
          }%, ${photo.y}%)`
        );
        console.log(
          `   Esquina: (${x.toFixed(1)}, ${y.toFixed(1)}) a (${(
            x + photoWidth
          ).toFixed(1)}, ${(y + photoHeight).toFixed(1)})`
        );
        console.log(`   ¿Cabe?: ${fitsInPage ? '✅ SÍ' : '❌ NO'}`);

        if (!fitsInPage) {
          console.log(`   ❌ PROBLEMA: Foto ${index + 1} sale de los límites`);
        }
      });

      // Verificar si todas las fotos caben
      const allFit = layoutData.every((photo, index) => {
        const photoWidth = (photo.width * width) / 100;
        const photoHeight = (photo.height * height) / 100;
        const centerX = (photo.x * width) / 100;
        const centerY = (photo.y * height) / 100;
        const x = centerX - photoWidth / 2;
        const y = centerY - photoHeight / 2;
        return (
          x >= 0 &&
          y >= 0 &&
          x + photoWidth <= width &&
          y + photoHeight <= height
        );
      });

      console.log(`\n🎯 RESULTADO GENERAL:`);
      console.log(`   ¿Todas las fotos caben?: ${allFit ? '✅ SÍ' : '❌ NO'}`);
      console.log(`   Total de fotos: ${layoutData.length}`);
    }
  }

  analyzeMultiplePhotosLayout() {
    console.log('\n📸 ANÁLISIS DE LAYOUTS DE MÚLTIPLES FOTOS:');
    console.log('============================================');

    const pageWidth = 100;
    const pageHeight = 100;

    // Ejemplo 1: Layout 2x3 (6 fotos)
    console.log('\n📋 LAYOUT 2x3 (6 fotos):');
    const layout2x3 =
      '.3,.3,0,.17|.3,.3,.47,.17|.3,.3,.94,.17|.3,.3,.06,.83|.3,.3,.53,.83|.3,.3,1,.83';
    this.analyzeMultiplePhotosFormat(
      layout2x3,
      pageWidth,
      pageHeight,
      '2x3 Grid'
    );

    // Ejemplo 2: Layout 2x2 (4 fotos)
    console.log('\n📋 LAYOUT 2x2 (4 fotos):');
    const layout2x2 =
      '.4,.4,0.25,0.25|.4,.4,0.75,0.25|.4,.4,0.25,0.75|.4,.4,0.75,0.75';
    this.analyzeMultiplePhotosFormat(
      layout2x2,
      pageWidth,
      pageHeight,
      '2x2 Grid'
    );

    // Ejemplo 3: Layout 3x1 (3 fotos horizontales)
    console.log('\n📋 LAYOUT 3x1 (3 fotos horizontales):');
    const layout3x1 = '.25,.6,0.17,0.5|.25,.6,0.5,0.5|.25,.6,0.83,0.5';
    this.analyzeMultiplePhotosFormat(
      layout3x1,
      pageWidth,
      pageHeight,
      '3x1 Horizontal'
    );

    // Ejemplo 4: Layout 1x3 (3 fotos verticales)
    console.log('\n📋 LAYOUT 1x3 (3 fotos verticales):');
    const layout1x3 = '.6,.25,0.5,0.17|.6,.25,0.5,0.5|.6,.25,0.5,0.83';
    this.analyzeMultiplePhotosFormat(
      layout1x3,
      pageWidth,
      pageHeight,
      '1x3 Vertical'
    );
  }

  analyzeMultiplePhotosFormat(config, pageWidth, pageHeight, description) {
    const layoutData = this.parseLayoutData(config);
    if (!layoutData || !Array.isArray(layoutData)) {
      console.log(`   ❌ Error parsing: ${description}`);
      return;
    }

    console.log(`   📸 ${description}:`);
    console.log(`   Config: ${config}`);
    console.log(`   Total fotos: ${layoutData.length}`);

    let allFit = true;
    layoutData.forEach((photo, index) => {
      const photoWidth = (photo.width * pageWidth) / 100;
      const photoHeight = (photo.height * pageHeight) / 100;
      const centerX = (photo.x * pageWidth) / 100;
      const centerY = (photo.y * pageHeight) / 100;
      const x = centerX - photoWidth / 2;
      const y = centerY - photoHeight / 2;

      const fitsInPage =
        x >= 0 &&
        y >= 0 &&
        x + photoWidth <= pageWidth &&
        y + photoHeight <= pageHeight;
      if (!fitsInPage) allFit = false;

      console.log(
        `     Foto ${index + 1}: ${photoWidth.toFixed(1)}x${photoHeight.toFixed(
          1
        )}px en (${x.toFixed(1)}, ${y.toFixed(1)}) - ${
          fitsInPage ? '✅' : '❌'
        }`
      );
    });

    console.log(
      `   Resultado: ${allFit ? '✅ Todas caben' : '❌ Algunas no caben'}`
    );
  }

  analyzeConfigurationOptions() {
    console.log('FotobookEditor: Analyzing configuration options');

    const pageWidth = 100;
    const pageHeight = 100;

    // Opción 1: Mantener el formato actual pero ajustar Y para que quepa
    console.log('\n=== OPCIÓN 1: Ajustar Y para que quepa ===');
    const option1 = this.analyzeFormat(
      '0.8,0.8,0.5,0.5',
      pageWidth,
      pageHeight,
      'Centrado vertical'
    );

    // Opción 2: Reducir el tamaño para que quepa en cualquier posición
    console.log('\n=== OPCIÓN 2: Reducir tamaño para seguridad ===');
    const option2 = this.analyzeFormat(
      '0.6,0.6,0.5,0.25',
      pageWidth,
      pageHeight,
      'Tamaño reducido'
    );

    // Opción 3: Usar márgenes mínimos
    console.log('\n=== OPCIÓN 3: Márgenes mínimos ===');
    const option3 = this.analyzeFormat(
      '0.9,0.9,0.5,0.5',
      pageWidth,
      pageHeight,
      'Máximo tamaño centrado'
    );

    // Opción 4: Formato original con validación
    console.log('\n=== OPCIÓN 4: Formato original con validación ===');
    const option4 = this.analyzeFormat(
      '0.8,0.8,0.5,0.25',
      pageWidth,
      pageHeight,
      'Original con ajuste'
    );

    // Opción 5: Sistema de coordenadas desde esquina
    console.log('\n=== OPCIÓN 5: Coordenadas desde esquina ===');
    const option5 = this.analyzeFormatFromCorner(
      '0.8,0.8,0.1,0.1',
      pageWidth,
      pageHeight,
      'Desde esquina'
    );

    return { option1, option2, option3, option4, option5 };
  }

  analyzeFormat(config, pageWidth, pageHeight, description) {
    const layoutData = this.parseLayoutData(config);
    if (!layoutData) return null;

    const photo = layoutData[0];
    const photoWidth = (photo.width * pageWidth) / 100;
    const photoHeight = (photo.height * pageHeight) / 100;
    const centerX = (photo.x * pageWidth) / 100;
    const centerY = (photo.y * pageHeight) / 100;
    const x = centerX - photoWidth / 2;
    const y = centerY - photoHeight / 2;

    const fitsInPage =
      x >= 0 &&
      y >= 0 &&
      x + photoWidth <= pageWidth &&
      y + photoHeight <= pageHeight;

    console.log(`📋 ${description}:`);
    console.log(`   Config: ${config}`);
    console.log(
      `   Tamaño: ${photoWidth.toFixed(1)}x${photoHeight.toFixed(1)}px`
    );
    console.log(
      `   Posición: (${x.toFixed(1)}, ${y.toFixed(1)}) a (${(
        x + photoWidth
      ).toFixed(1)}, ${(y + photoHeight).toFixed(1)})`
    );
    console.log(`   ¿Cabe?: ${fitsInPage ? '✅ SÍ' : '❌ NO'}`);

    if (!fitsInPage) {
      console.log(`   ❌ PROBLEMA: Sale de la página`);
      console.log(
        `   💡 SUGERENCIA: Ajustar Y a ${(
          (photoHeight / 2 / pageHeight) *
          100
        ).toFixed(1)}% o más`
      );
    }

    return { config, fitsInPage, photoWidth, photoHeight, x, y };
  }

  analyzeFormatFromCorner(config, pageWidth, pageHeight, description) {
    // Interpretar como coordenadas desde esquina superior izquierda
    const numbers = config.split(',').map((n) => parseFloat(n.trim()));
    if (numbers.length < 4) return null;

    const photoWidth = numbers[0] * pageWidth; // 0.8 -> 80px
    const photoHeight = numbers[1] * pageHeight; // 0.8 -> 80px
    const x = numbers[2] * pageWidth; // 0.1 -> 10px (desde izquierda)
    const y = numbers[3] * pageHeight; // 0.1 -> 10px (desde arriba)

    const fitsInPage =
      x >= 0 &&
      y >= 0 &&
      x + photoWidth <= pageWidth &&
      y + photoHeight <= pageHeight;

    console.log(`📋 ${description}:`);
    console.log(`   Config: ${config} (desde esquina)`);
    console.log(
      `   Tamaño: ${photoWidth.toFixed(1)}x${photoHeight.toFixed(1)}px`
    );
    console.log(
      `   Posición: (${x.toFixed(1)}, ${y.toFixed(1)}) a (${(
        x + photoWidth
      ).toFixed(1)}, ${(y + photoHeight).toFixed(1)})`
    );
    console.log(`   ¿Cabe?: ${fitsInPage ? '✅ SÍ' : '❌ NO'}`);

    return {
      config,
      fitsInPage,
      photoWidth,
      photoHeight,
      x,
      y,
      fromCorner: true
    };
  }

  validateAndAdjustFormat(layoutData, pageWidth, pageHeight) {
    if (!Array.isArray(layoutData)) return layoutData;

    return layoutData.map((photo) => {
      const photoWidth = (photo.width * pageWidth) / 100;
      const photoHeight = (photo.height * pageHeight) / 100;
      const centerX = (photo.x * pageWidth) / 100;
      const centerY = (photo.y * pageHeight) / 100;
      const x = centerX - photoWidth / 2;
      const y = centerY - photoHeight / 2;

      // Verificar si la foto cabe en la página
      const fitsInPage =
        x >= 0 &&
        y >= 0 &&
        x + photoWidth <= pageWidth &&
        y + photoHeight <= pageHeight;

      if (!fitsInPage) {
        console.log(
          'FotobookEditor: Format adjustment needed for photo:',
          photo
        );

        // Crear una copia ajustada
        const adjustedPhoto = { ...photo };

        // Ajustar X si sale por los lados
        if (x < 0) {
          // La foto sale por la izquierda, centrarla
          adjustedPhoto.x = (photoWidth / 2 / pageWidth) * 100;
          console.log(`   Ajustado X de ${photo.x}% a ${adjustedPhoto.x}%`);
        } else if (x + photoWidth > pageWidth) {
          // La foto sale por la derecha, centrarla
          adjustedPhoto.x = ((pageWidth - photoWidth / 2) / pageWidth) * 100;
          console.log(`   Ajustado X de ${photo.x}% a ${adjustedPhoto.x}%`);
        }

        // Ajustar Y si sale por arriba o abajo
        if (y < 0) {
          // La foto sale por arriba, centrarla
          adjustedPhoto.y = (photoHeight / 2 / pageHeight) * 100;
          console.log(`   Ajustado Y de ${photo.y}% a ${adjustedPhoto.y}%`);
        } else if (y + photoHeight > pageHeight) {
          // La foto sale por abajo, centrarla
          adjustedPhoto.y = ((pageHeight - photoHeight / 2) / pageHeight) * 100;
          console.log(`   Ajustado Y de ${photo.y}% a ${adjustedPhoto.y}%`);
        }

        return adjustedPhoto;
      }

      return photo;
    });
  }

  showConfigurationRecommendations() {
    console.log('\n🎯 RECOMENDACIONES DE CONFIGURACIÓN:');
    console.log('=====================================');

    console.log('\n📋 OPCIÓN RECOMENDADA 1: Centrado automático');
    console.log('   Formato: "0.8,0.8,0.5,0.5"');
    console.log('   Descripción: Foto 80% del tamaño, centrada en la página');
    console.log('   Ventajas: Siempre cabe, posición predecible');
    console.log('   Uso: Para fotos principales centradas');

    console.log('\n📋 OPCIÓN RECOMENDADA 2: Tamaño seguro');
    console.log('   Formato: "0.6,0.6,0.5,0.5"');
    console.log('   Descripción: Foto 60% del tamaño, centrada');
    console.log('   Ventajas: Muy seguro, margen amplio');
    console.log('   Uso: Para fotos con márgenes generosos');

    console.log('\n📋 OPCIÓN RECOMENDADA 3: Múltiples fotos');
    console.log('   Formato: "0.4,0.4,0.25,0.25" y "0.4,0.4,0.75,0.25"');
    console.log('   Descripción: Dos fotos de 40% en la parte superior');
    console.log('   Ventajas: Permite múltiples fotos por página');
    console.log('   Uso: Para páginas con varias fotos');

    console.log('\n📋 OPCIÓN RECOMENDADA 4: Coordenadas desde esquina');
    console.log('   Formato: "0.8,0.8,0.1,0.1"');
    console.log('   Descripción: Foto 80% desde esquina superior izquierda');
    console.log('   Ventajas: Posición fija, fácil de calcular');
    console.log('   Uso: Para fotos en posiciones específicas');

    console.log('\n📸 LAYOUTS DE MÚLTIPLES FOTOS RECOMENDADOS:');
    console.log(
      '   Grid 2x2: ".4,.4,0.25,0.25|.4,.4,0.75,0.25|.4,.4,0.25,0.75|.4,.4,0.75,0.75"'
    );
    console.log(
      '   Grid 2x3: ".3,.3,0,.17|.3,.3,.47,.17|.3,.3,.94,.17|.3,.3,.06,.83|.3,.3,.53,.83|.3,.3,1,.83"'
    );
    console.log(
      '   Horizontal 3x1: ".25,.6,0.17,0.5|.25,.6,0.5,0.5|.25,.6,0.83,0.5"'
    );
    console.log(
      '   Vertical 1x3: ".6,.25,0.5,0.17|.6,.25,0.5,0.5|.6,.25,0.5,0.83"'
    );

    console.log('\n⚠️  PROBLEMA CON FORMATO ORIGINAL:');
    console.log('   Formato: "0.8,0.8,0.5,0.25"');
    console.log('   Problema: Y=25% hace que la foto salga por arriba');
    console.log('   Solución: Cambiar Y a 50% o usar validación automática');

    console.log('\n🔧 IMPLEMENTACIÓN ACTUAL:');
    console.log('   - Validación automática: Ajusta formatos que no caben');
    console.log('   - Punto de transformación: Centro de la foto');
    console.log('   - Escala: 0-1 convertida a porcentajes');
    console.log('   - Múltiples fotos: Separadas por |');
    console.log('   - Logging: Muestra ajustes realizados');
  }

  navigateSheet(direction) {
    console.log(`FotobookEditor: Navigating sheet with direction ${direction}`);
    // Avanzar en pares (2 páginas = 1 hoja a la vez)
    const newPage = this.currentPage + direction * 2;
    const totalPages = this.config.totalSheets || 20;

    if (newPage >= 1 && newPage <= totalPages) {
      this.goToPage(newPage);
    }
  }

  goToPage(page) {
    this.currentPage = page;
    this.updatePageDisplay();

    // Actualizar indicadores de las páginas del libro
    this.updateBookPageIndicators();

    // Actualizar tooltips de las páginas
    this.updatePageTooltips();

    // Seleccionar por defecto la hoja izquierda al cambiar de página
    this.selectBookSheet(1);

    // Cargar formato seleccionado para esta página
    const selectedFormat = this.selectedFormats[page];
    if (selectedFormat) {
      // Encontrar y seleccionar el formato correspondiente
      const formatOption = document.querySelector(
        `.sheet-format-option[data-sheet-id="${selectedFormat.id}"]`
      );
      if (formatOption) {
        // Seleccionar visualmente el formato
        document.querySelectorAll('.sheet-format-option').forEach((opt) => {
          opt.classList.remove('selected');
        });

        formatOption.classList.add('selected');

        // Actualizar vista previa
        this.updateDoublePagePreview(selectedFormat);
      }
    } else {
      // Limpiar selección si no hay formato para esta página
      document.querySelectorAll('.sheet-format-option').forEach((opt) => {
        opt.classList.remove('selected');
      });

      // Actualizar vista previa con formato vacío
      this.updateDoublePagePreview(null);
    }
  }

  updatePageDisplay() {
    const currentSheetEl = document.getElementById('current-sheet');
    const totalSheetsEl = document.getElementById('total-sheets');
    const prevSheetBtn = document.getElementById('prev-sheet');
    const nextSheetBtn = document.getElementById('next-sheet');
    const prevSheetNumberEl = document.getElementById('prev-sheet-number');
    const nextSheetNumberEl = document.getElementById('next-sheet-number');

    const totalPages = this.config.totalSheets || 20;
    const totalDualSheets = totalPages / 2;
    const currentSheetIndex = Math.ceil(this.currentPage / 2);
    const previousSheetIndex = currentSheetIndex - 1;
    const nextSheetIndex = currentSheetIndex + 1;

    if (currentSheetEl) {
      currentSheetEl.textContent = currentSheetIndex;
    }

    if (totalSheetsEl) {
        totalSheetsEl.textContent = totalDualSheets;
    }

    if (prevSheetBtn) {
      prevSheetBtn.disabled = this.currentPage <= 1;
      if (this.currentPage <= 1) {
        prevSheetBtn.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        prevSheetBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    }

    if (prevSheetNumberEl) {
      if (previousSheetIndex >= 1) {
        prevSheetNumberEl.textContent = previousSheetIndex;
        prevSheetNumberEl.classList.remove('opacity-0');
      } else {
        prevSheetNumberEl.textContent = '';
        prevSheetNumberEl.classList.add('opacity-0');
      }
    }

    if (nextSheetBtn) {
      const nextPairPage = this.currentPage + 2;
      nextSheetBtn.disabled = nextPairPage > totalPages;
      if (nextPairPage > totalPages) {
        nextSheetBtn.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        nextSheetBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    }

    if (nextSheetNumberEl) {
      if (nextSheetIndex <= totalDualSheets) {
        nextSheetNumberEl.textContent = nextSheetIndex;
        nextSheetNumberEl.classList.remove('opacity-0');
      } else {
        nextSheetNumberEl.textContent = '';
        nextSheetNumberEl.classList.add('opacity-0');
      }
    }

    this.updateFormatIndicator();
  }

  toggleGridView() {
    // Ahora el cambio de sección lo maneja FotobookMenu a través de data-target="#grid-view-section"
    // Este método puede ser llamado por shortcuts de teclado (Esc) para volver a Interiores
    const interiorsBtn = document.querySelector('button[data-target="#photos-section"]');
    if (interiorsBtn) {
      interiorsBtn.click();
    }
  }

  async renderSheetGrid() {
    const gridContainer = document.getElementById('sheets-grid-container');
    if (!gridContainer) return;

    gridContainer.innerHTML = '';
    const totalPages = this.config.totalSheets || 20;
    const totalDualSheets = totalPages / 2;

    for (let i = 1; i <= totalDualSheets; i++) {
        const leftPage = (i * 2) - 1;
        const sheetCard = document.createElement('div');
        sheetCard.className = `cursor-pointer sheet-grid-item border-2 rounded-lg p-3 transition-all duration-300 hover:shadow-md ${this.currentPage === leftPage ? 'border-petrol-500 bg-petrol-50' : 'border-gray-200 bg-white hover:border-petrol-300'}`;
        
        const previewId = `grid-preview-${i}`;
        const isFilled = this.isSheetFilled(i);
        
        sheetCard.innerHTML = `
            <div class="flex flex-col gap-2">
                <div class="relative w-full overflow-hidden bg-white shadow-sm border border-gray-100" id="${previewId}" style="aspect-ratio: 2/1">
                    ${isFilled ? `
                    <div class="absolute top-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center z-10 shadow-sm border border-white">
                        <i class="fas fa-check text-[10px]"></i>
                    </div>
                    ` : ''}
                </div>
                <div class="flex justify-between items-center px-1">
                    <span class="text-xs font-bold ${this.currentPage === leftPage ? 'text-petrol-700' : 'text-gray-500'}">Hoja ${i}</span>
                    <i class="fas fa-chevron-right text-[10px] ${this.currentPage === leftPage ? 'text-petrol-400' : 'text-gray-300'}"></i>
                </div>
            </div>
        `;

        sheetCard.addEventListener('click', () => {
          this.goToPage(leftPage);
          // Cambiar a la pestaña de interiores
          const interiorsBtn = document.querySelector('button[data-target="#photos-section"]');
          if (interiorsBtn) {
            interiorsBtn.click();
          }
        });

        gridContainer.appendChild(sheetCard);

        const previewContainer = document.getElementById(previewId);
        if (previewContainer) {
            this.renderSheetMiniPreview(previewContainer, i);
        }
    }
  }

  renderSheetMiniPreview(container, sheetNumber) {
    const leftPage = (sheetNumber * 2) - 1;
    const rightPage = sheetNumber * 2;
    
    const leftFormat = this.selectedFormats[leftPage];
    const rightFormat = this.selectedFormats[rightPage];

    const dualPageWidth = 400;
    const dualPageHeight = 200;
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${dualPageWidth} ${dualPageHeight}`);
    svg.setAttribute('class', 'w-full h-full');
    
    // Usar la configuración real asignada o fallback por defecto
    const leftConfig = leftFormat?.config || "1;0,0,100,100";
    const rightConfig = rightFormat?.config || "1;0,0,100,100";

    const leftPreview = this.generateSVGPreview(leftConfig, dualPageWidth/2, dualPageHeight, { 
      isThumbnail: true, 
      pageNumber: leftPage,
      showIndices: false
    });
    
    const rightPreview = this.generateSVGPreview(rightConfig, dualPageWidth/2, dualPageHeight, { 
      isThumbnail: true, 
      pageNumber: rightPage,
      showIndices: false
    });

    const gLeft = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    gLeft.innerHTML = leftPreview;
    
    const gRight = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    gRight.setAttribute('transform', `translate(${dualPageWidth/2}, 0)`);
    gRight.innerHTML = rightPreview;

    svg.appendChild(gLeft);
    svg.appendChild(gRight);
    
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', dualPageWidth/2); line.setAttribute('y1', 0);
    line.setAttribute('x2', dualPageWidth/2); line.setAttribute('y2', dualPageHeight);
    line.setAttribute('stroke', '#eee'); line.setAttribute('stroke-width', '1');
    svg.appendChild(line);

    container.appendChild(svg);
    
    // Aplicar fotos colocadas si existen para que se vean en la miniatura
    setTimeout(() => {
        this.applyPlacedPhotosToPage(svg.querySelector('g:first-child'), leftPage);
        this.applyPlacedPhotosToPage(svg.querySelector('g:nth-child(2)'), rightPage);
    }, 0);
  }

  isSheetFilled(sheetNumber) {
    const leftPage = (sheetNumber * 2) - 1;
    const rightPage = sheetNumber * 2;
    
    // Verificar página izquierda
    const leftFormat = this.selectedFormats[leftPage];
    const leftSlots = leftFormat ? parseInt(leftFormat.noPhotos, 10) || 0 : 0;
    const leftFilled = this.placedPhotos[leftPage] ? Object.keys(this.placedPhotos[leftPage]).length : 0;
    
    // Verificar página derecha
    const rightFormat = this.selectedFormats[rightPage];
    const rightSlots = rightFormat ? parseInt(rightFormat.noPhotos, 10) || 0 : 0;
    const rightFilled = this.placedPhotos[rightPage] ? Object.keys(this.placedPhotos[rightPage]).length : 0;
    
    if (leftSlots === 0 && rightSlots === 0) return false;
    
    return leftFilled >= leftSlots && rightFilled >= rightSlots;
  }

  toggleSheetFormats() {
    const container = document.getElementById('sheet-formats-container');
    const toggleBtn = document.getElementById('toggle-sheet-formats');
    if (!container) return;

    const isCollapsed = container.classList.contains('max-h-0');

    if (isCollapsed) {
        container.classList.remove('max-h-0', 'opacity-0');
        container.classList.add('max-h-40', 'opacity-100');
        if (toggleBtn) {
            const icon = toggleBtn.querySelector('i');
            if (icon) icon.className = 'fas fa-chevron-down text-xs transition-transform duration-300';
            toggleBtn.classList.remove('rotate-180');
        }
    } else {
        container.classList.add('max-h-0', 'opacity-0');
        container.classList.remove('max-h-40', 'opacity-100');
        toggleBtn?.classList.add('rotate-180');
    }
  }

  toggleCoverFormats() {
    const container = document.getElementById('cover-formats-container');
    const toggleBtn = document.getElementById('toggle-cover-formats');
    if (!container) return;

    const isCollapsed = container.classList.contains('max-h-0');

    if (isCollapsed) {
        container.classList.remove('max-h-0', 'opacity-0');
        container.classList.add('max-h-40', 'opacity-100');
        toggleBtn?.classList.remove('rotate-180');
    } else {
        container.classList.add('max-h-0', 'opacity-0');
        container.classList.remove('max-h-40', 'opacity-100');
        toggleBtn?.classList.add('rotate-180');
    }
  }

  updateCoverSheetDisplay() {
    const currentCoverSheetEl = document.getElementById('current-cover-sheet');
    const prevCoverSheetBtn = document.getElementById('prev-cover-sheet');
    const nextCoverSheetBtn = document.getElementById('next-cover-sheet');
    const totalCoverSheetsEl = document.getElementById('total-cover-sheets');

    const currentCoverSheet = this.currentCoverSheet || 1;
    const totalCoverSheets = 1; // Puede ajustarse según la lógica de negocio

    if (currentCoverSheetEl) {
      currentCoverSheetEl.textContent = currentCoverSheet;
    }

    if (totalCoverSheetsEl) {
      totalCoverSheetsEl.textContent = totalCoverSheets;
    }

    // Actualizar botones de navegación de covers
    if (prevCoverSheetBtn) {
      prevCoverSheetBtn.disabled = currentCoverSheet <= 1;
      if (currentCoverSheet <= 1) {
        prevCoverSheetBtn.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        prevCoverSheetBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    }

    if (nextCoverSheetBtn) {
      nextCoverSheetBtn.disabled = currentCoverSheet >= totalCoverSheets;
      if (currentCoverSheet >= totalCoverSheets) {
        nextCoverSheetBtn.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        nextCoverSheetBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    }
  }

  updateFormatIndicator() {
    const selectedFormat = this.selectedFormats[this.currentPage];
    const selectedFormatEl = document.getElementById('selected-format');

    if (selectedFormatEl) {
      if (selectedFormat) {
        // Obtener información detallada de la página
        const pageInfo = this.getPageFormatInfo(this.currentPage);

        if (pageInfo) {
          const formatName = `${selectedFormat.noPhotos} foto(s)`;
          const cycleInfo =
            pageInfo.cycleNumber > 1 ? ` (Ciclo ${pageInfo.cycleNumber})` : '';
          selectedFormatEl.textContent = `${formatName} - Página ${this.currentPage}${cycleInfo}`;
        } else {
          selectedFormatEl.textContent = `${selectedFormat.noPhotos} foto(s) - Página ${this.currentPage}`;
        }

        selectedFormatEl.className = 'text-sm font-medium text-green-600 ml-2';
      } else {
        selectedFormatEl.textContent = 'Sin formato - Selecciona un formato';
        selectedFormatEl.className = 'text-sm font-medium text-orange-600 ml-2';
      }
    }
  }
  // Método para obtener los datos del fotobook
  getFotobookData() {
    return {
      selectedFormats: this.selectedFormats,
      selectedPhotos: this.selectedPhotos,
      totalPages: this.config.totalSheets
    };
  }

  // Obtener los índices de archivos que ya están en uso
  getUsedFileIndices() {
    const usedIndices = new Set();
    Object.values(this.placedPhotos).forEach((pageSlots) => {
      Object.values(pageSlots).forEach((slotData) => {
        if (slotData && slotData.fileIndex !== undefined) {
          usedIndices.add(Number(slotData.fileIndex));
        }
      });
    });
    return Array.from(usedIndices);
  }

  // Agregar una foto al primer slot vacío que encuentre (empezando por la página actual)
  addPhotoToFirstEmptySlot(fileIndex) {
    const totalPages = this.config.totalSheets || 20;

    // 1. Buscar en la página actual y su compañera (spread actual)
    const currentSpread = [this.currentPage, this.currentPage + 1];
    for (const page of currentSpread) {
      if (page > totalPages) continue;
      // La última hoja está reservada para el colofón
      if (page === totalPages) continue;

      const pageKey = page;
      const format = this.selectedFormats[page];
      if (!format || !format.config) continue;

      const layoutData = this.parseLayoutData(format.config);
      const numSlots = layoutData.length;

      for (let slotIndex = 0; slotIndex < numSlots; slotIndex++) {
        const isOccupied =
          this.placedPhotos[pageKey] && this.placedPhotos[pageKey][slotIndex];
        if (!isOccupied) {
          const pageElId =
            page === this.currentPage
              ? 'left-sheet-preview'
              : 'right-sheet-preview';
          const pageEl = document.getElementById(pageElId);
          if (pageEl) {
            this.handlePhotoDropOnSlot(pageEl, slotIndex, fileIndex);
            this.updatePhotoSelectorUsedStatus();
            return true;
          }
        }
      }
    }

    // 2. Si no hay en el spread actual, buscar en el resto del libro
    for (let page = 1; page <= totalPages; page++) {
      // La última hoja está reservada para el colofón
      if (page === totalPages) continue;

      const pageKey = page;
      const format = this.selectedFormats[page];
      if (!format || !format.config) continue;

      const layoutData = this.parseLayoutData(format.config);
      const numSlots = layoutData.length;

      for (let slotIndex = 0; slotIndex < numSlots; slotIndex++) {
        const isOccupied =
          this.placedPhotos[pageKey] && this.placedPhotos[pageKey][slotIndex];
        if (!isOccupied) {
          // Ir a la página donde encontramos el slot vacío
          const targetLeftPage = page % 2 === 0 ? page - 1 : page;
          this.goToPage(targetLeftPage);

          // Capturar los valores antes del timeout para evitar condiciones de carrera
          const capturedPage = page;
          const capturedSlotIndex = slotIndex;
          const capturedTargetLeftPage = targetLeftPage;
          setTimeout(() => {
            // Asegurar que seguimos en la página correcta antes de colocar la foto
            if (this.currentPage !== capturedTargetLeftPage) {
              this.goToPage(capturedTargetLeftPage);
            }
            const pageElId =
              capturedPage % 2 !== 0 ? 'left-sheet-preview' : 'right-sheet-preview';
            const pageEl = document.getElementById(pageElId);
            if (pageEl) {
              this.handlePhotoDropOnSlot(pageEl, capturedSlotIndex, fileIndex);
              this.updatePhotoSelectorUsedStatus();
            }
          }, 100);
          return true;
        }
      }
    }

    console.warn('FotobookEditor: No se encontraron slots vacíos');
    return false;
  }

  showConfirmationModal({
    title,
    message,
    confirmText = 'Aceptar',
    cancelText = 'Cancelar'
  }) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className =
        'fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4';

      overlay.innerHTML = `
        <div class="w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="magic-confirm-title" aria-describedby="magic-confirm-message">
          <div class="px-6 py-5 border-b border-gray-100">
            <h3 id="magic-confirm-title" class="text-lg font-semibold text-gray-900"></h3>
          </div>
          <div class="px-6 py-5">
            <p id="magic-confirm-message" class="text-sm text-gray-700 whitespace-pre-line"></p>
          </div>
          <div class="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
            <button type="button" data-action="cancel" class="btn info">
              ${cancelText}
            </button>
            <button type="button" data-action="confirm" class="btn">
              ${confirmText}
            </button>
          </div>
        </div>
      `;

      const titleEl = overlay.querySelector('#magic-confirm-title');
      const messageEl = overlay.querySelector('#magic-confirm-message');
      const cancelBtn = overlay.querySelector('[data-action="cancel"]');
      const confirmBtn = overlay.querySelector('[data-action="confirm"]');

      if (titleEl) titleEl.textContent = title;
      if (messageEl) messageEl.textContent = message;

      const cleanup = (result) => {
        document.removeEventListener('keydown', onKeyDown);
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        resolve(result);
      };

      const onKeyDown = (event) => {
        if (event.key === 'Escape') {
          cleanup(false);
        }
      };

      cancelBtn?.addEventListener('click', () => cleanup(false));
      confirmBtn?.addEventListener('click', () => cleanup(true));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          cleanup(false);
        }
      });

      document.addEventListener('keydown', onKeyDown);
      document.body.appendChild(overlay);
      confirmBtn?.focus();
    });
  }

  // Armado Automático (Magic Fotobook)
  async magicFill() {
    const readyPhotos = window.fotobookPhotoSelector.selectedFiles.filter(
      (p) => p.status === 'ready'
    );
    const totalPhotos = readyPhotos.length;

    if (totalPhotos === 0) {
      if (window.appAlerts?.warning) {
        window.appAlerts.warning('Vaya, parece que aún no has cargado fotos. Sube algunas primero.');
      } else {
        alert('Vaya, parece que aún no has cargado fotos. Sube algunas primero.');
      }
      return;
    }

    const shouldProceed = await this.showConfirmationModal({
      title: 'Armado automático',
      message:
        '¿Quieres que armemos tu fotobook automáticamente?\n\nEsto reemplazará las fotos que ya hayas colocado en los interiores (la portada se mantiene).',
      confirmText: 'Sí, armar ahora',
      cancelText: 'Cancelar'
    });

    if (!shouldProceed) {
      return;
    }

    // Limpiar fotos de interiores, preservando la portada ya asignada
    const savedCoverPhotos = this.placedPhotos['cover-front']
      ? { ...this.placedPhotos['cover-front'] }
      : null;
    this.placedPhotos = {};
    if (savedCoverPhotos && Object.keys(savedCoverPhotos).length > 0) {
      this.placedPhotos['cover-front'] = savedCoverPhotos;
    }
    this.totalRequiredSlots = null; // Forzar recálculo después de reasignar formatos

    // La última hoja es el colofón y no recibe fotos del usuario
    const totalPages = (this.config.totalSheets || 20) - 1;
    let shouldRepeat = false;

    if (totalPhotos < totalPages) {
      shouldRepeat = await this.showConfirmationModal({
        title: 'Fotos insuficientes para completar el libro',
        message: `Tienes ${totalPhotos} fotos para ${totalPages} páginas.\n\n¿Quieres que repitamos algunas fotos para llenar todo el libro?`,
        confirmText: 'Repetir fotos',
        cancelText: 'Dejar huecos'
      });
    }

    // Obtener formatos disponibles agrupados por número de fotos
    const formatOptions = Array.from(
      document.querySelectorAll('.sheet-format-option:not([data-double="true"])')
    );
    const formatsBySlots = {};
    formatOptions.forEach((opt) => {
      const slots = parseInt(opt.dataset.noPhotos, 10) || 0;
      if (!formatsBySlots[slots]) formatsBySlots[slots] = [];
      formatsBySlots[slots].push(opt);
    });

    // Mantener una rotación por cantidad de slots para evitar resultados repetitivos.
    const formatRotationBySlots = {};
    Object.keys(formatsBySlots).forEach((slotKey) => {
      formatRotationBySlots[slotKey] = 0;
    });

    const maxSlotsPossible = Math.max(
      ...Object.keys(formatsBySlots).map((k) => parseInt(k, 10))
    );

    // Determinar cuántas fotos por página (distribución balanceada)
    const distribution = new Array(totalPages).fill(0);

    if (totalPhotos >= totalPages) {
      const base = Math.floor(totalPhotos / totalPages);
      const extra = totalPhotos % totalPages;

      for (let i = 0; i < totalPages; i++) {
        distribution[i] = Math.min(base + (i < extra ? 1 : 0), maxSlotsPossible);
      }
    } else if (shouldRepeat) {
      // Repetir fotos: mínimo 1 por página
      for (let i = 0; i < totalPages; i++) {
        distribution[i] = 1;
      }
    } else {
      // Dejar huecos: 1 por página hasta agotar
      for (let i = 0; i < totalPhotos; i++) {
        distribution[i] = 1;
      }
    }

    let photoCounter = 0;
    let filledSlotsCount = 0;

    // Aplicar formatos y colocar fotos (sin incluir la última hoja reservada para colofón)
    for (let page = 1; page <= totalPages; page++) {
      const targetSlots = distribution[page - 1];
      if (targetSlots === 0) continue;

      let bestFormat = null;
      let selectedSlotCount = null;
      for (let s = targetSlots; s >= 1; s--) {
        if (formatsBySlots[s] && formatsBySlots[s].length > 0) {
          selectedSlotCount = s;
          break;
        }
      }

      if (selectedSlotCount !== null) {
        const candidateFormats = formatsBySlots[selectedSlotCount];
        const rotationIndex = formatRotationBySlots[selectedSlotCount] || 0;
        bestFormat = candidateFormats[rotationIndex % candidateFormats.length];
        formatRotationBySlots[selectedSlotCount] = rotationIndex + 1;
      }

      if (bestFormat) {
        const formatInfo = {
          id: bestFormat.dataset.sheetId,
          noPhotos: parseInt(bestFormat.dataset.noPhotos, 10) || 0,
          config: bestFormat.dataset.config,
          fullPage: bestFormat.dataset.fullPage === 'true'
        };

        this.selectedFormats[page] = formatInfo;

        const actualSlots = formatInfo.noPhotos;
        if (!this.placedPhotos[page]) this.placedPhotos[page] = {};

        for (let s = 0; s < actualSlots; s++) {
          const photoToPlaceIndex = shouldRepeat
            ? photoCounter % totalPhotos
            : photoCounter;

          if (photoToPlaceIndex < totalPhotos) {
            const photo = readyPhotos[photoToPlaceIndex];
            this.placedPhotos[page][s] = {
              url: photo.thumbUrl,
              fileIndex: window.fotobookPhotoSelector.selectedFiles.indexOf(photo),
              photoId: photo.id
            };
            photoCounter++;
            filledSlotsCount++;
          }
        }
      }
    }

    // Refrescar la vista
    this.goToPage(1);
    this.updatePhotoSelectorUsedStatus();
    this.countFilledSlots();
    this.updateSendToPrintButtonState();

    // Mensaje final
    const activePages = distribution.filter((d) => d > 0).length;
    let message = `¡Listo! Hemos acomodado ${filledSlotsCount} fotos en ${activePages} páginas.`;

    if (shouldRepeat) {
      message += `\nSe han repetido algunas fotos para completar las ${totalPages} páginas del libro.`;
    } else if (photoCounter < totalPhotos) {
      const remaining = totalPhotos - photoCounter;
      message += `\n\nQuedaron ${remaining} fotos sin espacio (el libro está lleno con ${filledSlotsCount} fotos).`;
    } else if (activePages < totalPages) {
      const empty = totalPages - activePages;
      message += `\n\nSe usaron todas tus fotos. Te quedan ${empty} páginas vacías por rellenar.`;
    }

    if (window.appAlerts?.success) {
      window.appAlerts.success(message, { duration: 5 });
    } else {
      alert(message);
    }
  }

  // Notificar al selector de fotos para que actualice el estado visual de "usado"
  updatePhotoSelectorUsedStatus() {
    if (window.fotobookPhotoSelector && window.fotobookPhotoSelector.updateFileList) {
      window.fotobookPhotoSelector.updateFileList();
    }
  }

  getPageDimensions() {
    const pageWidth = this.config.pageWidth || 300;
    const pageHeight = this.config.pageHeight || 400;
    return { pageWidth, pageHeight };
  }

  // Obtener las dimensiones reales de página para renderizado de templates
  getRealPageDimensions() {
    const { pageWidth, pageHeight } = this.getPageDimensions();
    return {
      width: pageWidth,
      height: pageHeight,
      aspectRatio: pageWidth / pageHeight
    };
  }

  updatePageDimensions() {
    // Obtener las dimensiones reales del paquete
    const { pageWidth, pageHeight } = this.getPageDimensions();

    // Actualizar el aspect-ratio de cada página individualmente
    const pageContents = document.querySelectorAll('.page-content');
    pageContents.forEach((pageContent) => {
      const aspectRatio = pageWidth / pageHeight;
      pageContent.style.aspectRatio = aspectRatio.toString();
      // Asegurar escalado proporcional
      pageContent.style.maxWidth = '50%';
      pageContent.style.maxHeight = '100%';
      pageContent.style.width = 'auto';
      pageContent.style.height = 'auto';
    });

    // Actualizar el viewBox de todos los SVGs de página
    const pageSvgs = document.querySelectorAll('.page-content svg');
    pageSvgs.forEach((svg) => {
      svg.setAttribute('viewBox', `0 0 ${pageWidth} ${pageHeight}`);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    });

    // Actualizar las dimensiones en la configuración global
    this.config.pageWidth = pageWidth;
    this.config.pageHeight = pageHeight;

    console.log(
      `FotobookEditor: Page dimensions updated to ${pageWidth}x${pageHeight}`
    );

    // Forzar actualización de todos los templates con las nuevas dimensiones
    this.updateAllTemplates();
  }

  // Actualizar todos los templates para usar las nuevas dimensiones
  updateAllTemplates() {
    console.log('FotobookEditor: Updating all templates with new dimensions');

    // Actualizar previews de formatos
    this.renderSheetPreviews();

    // Actualizar páginas actuales si están renderizadas
    this.updateCurrentPages();
  }

  // Actualizar las hojas actualmente visibles
  updateCurrentPages() {
    const leftSheet = document.getElementById('left-sheet-preview');
    const rightSheet = document.getElementById('right-sheet-preview');

    if (leftSheet) {
      const leftContent = leftSheet.querySelector('svg');
      if (leftContent) {
        // Re-renderizar con las nuevas dimensiones
        this.renderCurrentPage();
      }
    }

    if (rightSheet) {
      const rightContent = rightSheet.querySelector('svg');
      if (rightContent) {
        // Re-renderizar con las nuevas dimensiones
        this.renderCurrentPage();
      }
    }
  }

  // Renderizar la página actual con el formato seleccionado
  renderCurrentPage() {
    console.log('FotobookEditor: Rendering current page');

    // Obtener el formato para la página actual
    const selectedFormat = this.selectedFormats[this.currentPage];

    if (selectedFormat) {
      console.log(
        `FotobookEditor: Rendering page ${this.currentPage} with format:`,
        selectedFormat
      );

      // Actualizar la vista previa de doble página
      this.updateDoublePagePreview(selectedFormat);

      // Actualizar indicadores de las páginas del libro
      this.updateBookPageIndicators();

      // Actualizar tooltips de las páginas
      this.updatePageTooltips();
    } else {
      console.log(
        `FotobookEditor: No format found for page ${this.currentPage}, using default`
      );

      // Renderizar con formato vacío si no hay formato seleccionado
      this.updateDoublePagePreview(null);
    }
  }

  // Diagnóstico de selección de páginas
  diagnosePageSelection() {
    console.log('\n🔍 DIAGNÓSTICO DE SELECCIÓN DE PÁGINAS:');
    console.log('=========================================');

    // Verificar elementos HTML
    const leftSheet = document.getElementById('left-sheet-preview');
    const rightSheet = document.getElementById('right-sheet-preview');

    console.log(
      `📄 Hoja izquierda encontrada: ${leftSheet ? '✅ SÍ' : '❌ NO'}`
    );
    console.log(
      `📄 Hoja derecha encontrada: ${rightSheet ? '✅ SÍ' : '❌ NO'}`
    );

    if (leftSheet) {
      console.log(`   - ID: ${leftSheet.id}`);
      console.log(`   - Clases: ${leftSheet.className}`);
      console.log(
        `   - Tiene event listener: ${leftSheet.onclick ? 'SÍ' : 'NO'}`
      );
    }

    if (rightSheet) {
      console.log(`   - ID: ${rightSheet.id}`);
      console.log(`   - Clases: ${rightSheet.className}`);
      console.log(
        `   - Tiene event listener: ${rightSheet.onclick ? 'SÍ' : 'NO'}`
      );
    }

    // Verificar configuración
    console.log(`\n⚙️ CONFIGURACIÓN:`);
    console.log(`   - Página actual: ${this.currentPage}`);
    console.log(`   - Total de hojas: ${this.config.totalSheets}`);
    console.log(`   - Página derecha calculada: ${this.currentPage + 1}`);
    console.log(
      `   - ¿Página derecha existe?: ${
        this.currentPage + 1 <= this.config.totalSheets ? '✅ SÍ' : '❌ NO'
      }`
    );

    // Verificar event listeners
    console.log(`\n🎯 EVENT LISTENERS:`);
    console.log(
      `   - Hoja izquierda (${leftSheet?.id}): ${
        leftSheet ? '✅ Elemento encontrado' : '❌ No encontrado'
      }`
    );
    console.log(
      `   - Hoja derecha (${rightSheet?.id}): ${
        rightSheet ? '✅ Elemento encontrado' : '❌ No encontrado'
      }`
    );

    // Verificar event listeners específicos
    const sheetContents = document.querySelectorAll('.sheet-content');
    console.log(
      `   - Elementos .sheet-content para drag/drop: ${sheetContents.length}`
    );

    // Verificar formatos asignados
    console.log(`\n📋 FORMATOS ASIGNADOS:`);
    console.log(
      `   - Página ${this.currentPage}: ${
        this.selectedFormats[this.currentPage]
          ? '✅ Formato asignado'
          : '❌ Sin formato'
      }`
    );
    console.log(
      `   - Página ${this.currentPage + 1}: ${
        this.selectedFormats[this.currentPage + 1]
          ? '✅ Formato asignado'
          : '❌ Sin formato'
      }`
    );

    console.log('=========================================\n');
  }

  // Método para verificar que las dimensiones se aplicaron correctamente
  verifyDimensions() {
    const { pageWidth, pageHeight } = this.getPageDimensions();
    const pageContents = document.querySelectorAll('.page-content');
    const pageSvgs = document.querySelectorAll('.page-content svg');

    console.log('\n🔍 VERIFICACIÓN DE DIMENSIONES:');
    console.log('============================');
    console.log(`📐 Dimensiones del paquete: ${pageWidth}x${pageHeight}`);
    console.log(`📄 Páginas encontradas: ${pageContents.length}`);
    console.log(`🎨 SVGs encontrados: ${pageSvgs.length}`);

    pageContents.forEach((pageContent, index) => {
      const aspectRatio = pageContent.style.aspectRatio;
      console.log(`   Página ${index + 1} aspect ratio: ${aspectRatio}`);
    });

    pageSvgs.forEach((svg, index) => {
      const viewBox = svg.getAttribute('viewBox');
      console.log(`   SVG ${index + 1} viewBox: ${viewBox}`);
    });

    const expectedAspectRatio = (pageWidth / pageHeight).toFixed(3);
    const firstPageAspectRatio = pageContents[0]?.style.aspectRatio;

    if (firstPageAspectRatio === expectedAspectRatio) {
      console.log('✅ Aspect ratio aplicado correctamente');
    } else {
      console.log(
        `❌ Aspect ratio incorrecto. Esperado: ${expectedAspectRatio}, Actual: ${firstPageAspectRatio}`
      );
    }

    return {
      pageWidth,
      pageHeight,
      aspectRatio: firstPageAspectRatio,
      pageCount: pageContents.length,
      svgCount: pageSvgs.length
    };
  }

  // Verificar que los templates se están escalando correctamente
  verifyTemplateScaling() {
    const realDimensions = this.getRealPageDimensions();
    const pageSvgs = document.querySelectorAll('.page-content svg');

    console.log('\n🔍 VERIFICACIÓN DE ESCALADO DE TEMPLATES:');
    console.log('==========================================');
    console.log(
      `📐 Dimensiones reales: ${realDimensions.width}x${realDimensions.height}`
    );
    console.log(`📄 SVGs de página encontrados: ${pageSvgs.length}`);

    pageSvgs.forEach((svg, index) => {
      const viewBox = svg.getAttribute('viewBox');
      const expectedViewBox = `0 0 ${realDimensions.width} ${realDimensions.height}`;

      console.log(`   SVG ${index + 1}:`);
      console.log(`     ViewBox actual: ${viewBox}`);
      console.log(`     ViewBox esperado: ${expectedViewBox}`);

      if (viewBox === expectedViewBox) {
        console.log(`     ✅ Escalado correcto`);
      } else {
        console.log(`     ❌ Escalado incorrecto`);
      }
    });

    // Verificar previews de formatos
    const sheetPreviews = document.querySelectorAll('.sheet-preview svg');
    console.log(`🎨 Previews de formato encontrados: ${sheetPreviews.length}`);

    sheetPreviews.forEach((preview, index) => {
      const viewBox = preview.getAttribute('viewBox');
      console.log(`   Preview ${index + 1} viewBox: ${viewBox}`);
    });

    return {
      realDimensions,
      pageSvgs: pageSvgs.length,
      sheetPreviews: sheetPreviews.length
    };
  }

  // Verificar que el escalado proporcional funcione correctamente
  verifyProportionalScaling() {
    const pageContents = document.querySelectorAll('.page-content');
    const container = document.getElementById('pages-container');

    console.log('\n🔍 VERIFICACIÓN DE ESCALADO PROPORCIONAL:');
    console.log('==========================================');
    console.log(`📦 Contenedor: ${container ? 'Encontrado' : 'No encontrado'}`);
    console.log(`📄 Páginas encontradas: ${pageContents.length}`);

    pageContents.forEach((page, index) => {
      const computedStyle = window.getComputedStyle(page);
      const aspectRatio = page.style.aspectRatio;
      const maxWidth = page.style.maxWidth;
      const maxHeight = page.style.maxHeight;

      console.log(`   Página ${index + 1}:`);
      console.log(`     Aspect ratio: ${aspectRatio}`);
      console.log(`     Max width: ${maxWidth}`);
      console.log(`     Max height: ${maxHeight}`);
      console.log(`     Display: ${computedStyle.display}`);
      console.log(`     Position: ${computedStyle.position}`);

      // Verificar que esté centrada
      const justifyContent = computedStyle.justifyContent;
      const alignItems = computedStyle.alignItems;
      console.log(`     Justify content: ${justifyContent}`);
      console.log(`     Align items: ${alignItems}`);

      if (justifyContent === 'center' && alignItems === 'center') {
        console.log(`     ✅ Centrada correctamente`);
      } else {
        console.log(`     ❌ No está centrada`);
      }
    });

    return {
      containerFound: !!container,
      pageCount: pageContents.length,
      allCentered: Array.from(pageContents).every((page) => {
        const style = window.getComputedStyle(page);
        return (
          style.justifyContent === 'center' && style.alignItems === 'center'
        );
      })
    };
  }

  updatePageSelectionIndicator() {
    // Determinar qué página del libro está seleccionada
    const isLeftPageSelected = this.selectedPage === this.currentPage;
    const isRightPageSelected = this.selectedPage === this.currentPage + 1;

    // Verificar si hay un formato de doble página aplicado
    const leftPageFormat = this.selectedFormats[this.currentPage];
    const rightPageFormat = this.selectedFormats[this.currentPage + 1];
    const isDoublePageFormat =
      leftPageFormat &&
      rightPageFormat &&
      leftPageFormat.id === rightPageFormat.id &&
      leftPageFormat.double === true;

    if (isDoublePageFormat) {
      console.log(
        `Páginas ${this.currentPage} y ${
          this.currentPage + 1
        } seleccionadas (formato doble página)`
      );
    } else if (isLeftPageSelected) {
      console.log(`Página ${this.selectedPage} seleccionada (izquierda)`);
    } else if (isRightPageSelected) {
      console.log(`Página ${this.selectedPage} seleccionada (derecha)`);
    }
  }

  updatePageTooltips() {
    // Obtener las hojas del libro
    const leftSheet = document.getElementById('left-sheet-preview');
    const rightSheet = document.getElementById('right-sheet-preview');

    if (!leftSheet || !rightSheet) {
      return;
    }

    // Verificar si hay un formato de doble hoja aplicado
    const leftSheetFormat = this.selectedFormats[this.currentPage];
    const rightSheetFormat = this.selectedFormats[this.currentPage + 1];
    const isDoubleSheetFormat =
      leftSheetFormat &&
      rightSheetFormat &&
      leftSheetFormat.id === rightSheetFormat.id &&
      leftSheetFormat.double === true;

    // Obtener los tooltips
    const leftTooltip = leftSheet.querySelector('.tooltip-text');
    const rightTooltip = rightSheet.querySelector('.tooltip-text');

    if (isDoubleSheetFormat) {
      // Si hay formato de doble hoja, mostrar advertencia en los tooltips
      if (leftTooltip) {
        leftTooltip.textContent = 'Cambiar formato romperá el doble hoja';
      }
      if (rightTooltip) {
        rightTooltip.textContent = 'Cambiar formato romperá el doble hoja';
      }

      // Cambiar el color del tooltip para indicar advertencia
      const leftTooltipContainer = leftSheet.querySelector('.sheet-tooltip');
      const rightTooltipContainer = rightSheet.querySelector('.sheet-tooltip');

      if (leftTooltipContainer) {
        leftTooltipContainer.className =
          'sheet-tooltip absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-orange-600 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none';
      }
      if (rightTooltipContainer) {
        rightTooltipContainer.className =
          'sheet-tooltip absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-orange-600 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none';
      }
    } else {
      // Si no hay formato de doble hoja, mostrar tooltip normal
      if (leftTooltip) {
        leftTooltip.textContent = 'Seleccionar hoja';
      }
      if (rightTooltip) {
        rightTooltip.textContent = 'Seleccionar hoja';
      }

      // Restaurar el color normal del tooltip
      const leftTooltipContainer = leftSheet.querySelector('.sheet-tooltip');
      const rightTooltipContainer = rightSheet.querySelector('.sheet-tooltip');

      if (leftTooltipContainer) {
        leftTooltipContainer.className =
          'sheet-tooltip absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none';
      }
      if (rightTooltipContainer) {
        rightTooltipContainer.className =
          'sheet-tooltip absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none';
      }
    }
  }
}
